import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTour } from '@reactour/tour';
import { downloadFromResponse } from '../utils/download';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { api, useAuth } from '../contexts/AuthContext';
import SupportButton from '../components/SupportButton';
import { getTourSteps, useTourPreferences } from '../components/ProductTour';
import { FeatureGuideModal, hasSeenGuide } from '../components/FeatureGuideModal';
import { RevenueRangesDialog, DropdownStandardizeDialog } from '../components/cleaning';
import TimeSavingsModal from '../components/TimeSavingsModal';
import * as htmlToImage from 'html-to-image';
import { 
  FileSpreadsheet, 
  ArrowLeft,
  ArrowRight,
  Check,
  Sparkles,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Type,
  Hash,
  AlertTriangle,
  AlertCircle,
  Loader2,
  Eye,
  Download,
  RefreshCw,
  Plus,
  Globe,
  Settings,
  Link as LinkIcon,
  X,
  Clipboard,
  DollarSign,
  Building2,
  List,
  Edit3,
  Trash2,
  Merge,
  User,
  Scissors,
  ExternalLink,
  Info,
  HelpCircle,
  Clock,
  ArrowUpCircle,
  Coins,
  Zap,
  Monitor,
  Lightbulb
} from 'lucide-react';

// Mobile detection hook - file cleaning is desktop-only
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      const mobileWidth = window.innerWidth < 768;
      const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsMobile(mobileWidth || (hasTouchScreen && window.innerWidth < 1024));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

// Fun CRM facts for download screen
const DOWNLOAD_FUN_FACTS = [
  "Clean data reduces email bounce rates by up to 28%.",
  "Companies with data quality programs see 66% more pipeline value.",
  "The average CRM database has 25-30% duplicate records.",
  "Poor data quality costs organizations $15 million annually.",
  "Sales reps spend 17% of their week on data entry tasks.",
  "Data-driven companies are 23x more likely to acquire customers.",
  "91% of CRM data is incomplete after just 1 year.",
  "Clean contact data improves campaign ROI by 40%+.",
  "Merging duplicates can reduce storage costs by 10-20%.",
  "Accurate CRM data increases win rates by 29%."
];

const DownloadFunFact = () => {
  const [factIndex, setFactIndex] = useState(0);
  
  useEffect(() => {
    // Set random initial index on mount
    setFactIndex(Math.floor(Math.random() * DOWNLOAD_FUN_FACTS.length));
    
    const interval = setInterval(() => {
      setFactIndex(prev => (prev + 1) % DOWNLOAD_FUN_FACTS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <p className="text-xs text-indigo-700 italic">
      {DOWNLOAD_FUN_FACTS[factIndex]}
    </p>
  );
};

const RULE_INFO = {
  // Text Formatting
  title_case: { name: 'Title Case', icon: <Type className="w-4 h-4" />, description: 'Convert to title case (John Doe)', category: 'text', example: { before: 'JOHN DOE', after: 'John Doe' } },
  lowercase: { name: 'Lowercase', icon: <Type className="w-4 h-4" />, description: 'Convert to all lowercase', category: 'text', example: { before: 'HELLO World', after: 'hello world' } },
  uppercase: { name: 'Uppercase', icon: <Type className="w-4 h-4" />, description: 'Convert to all uppercase', category: 'text', example: { before: 'hello world', after: 'HELLO WORLD' } },
  trim_whitespace: { name: 'Trim Whitespace', icon: <Type className="w-4 h-4" />, description: 'Remove extra spaces', category: 'text', 
    // Dynamic example based on column type
    getExample: (columnType) => {
      if (columnType === 'email') return { before: 'john @ gmail .com', after: 'john@gmail.com' };
      if (columnType === 'phone') return { before: '555 - 123 - 4567', after: '555-123-4567' };
      if (columnType === 'company' || columnType === 'text') return { before: 'Acme   Inc   Corp', after: 'Acme Inc Corp' };
      return { before: '  John   Doe  ', after: 'John Doe' };
    },
    example: { before: '  extra   spaces  ', after: 'extra spaces' }
  },
  split_full_name: { name: 'Split Full Name', icon: <Type className="w-4 h-4" />, description: 'Split into First Name & Last Name columns (Title Case)', category: 'extract', example: { before: 'john doe', after: 'First: John, Last: Doe' } },
  detect_gender: { name: 'Detect Gender', icon: <User className="w-4 h-4" />, description: 'Detect gender from name (Male/Female/Not sure)', category: 'extract', example: { before: 'John', after: 'Male' } },
  // Email & Domain
  email_fix: { name: 'Email Fix', icon: <Mail className="w-4 h-4" />, description: 'Fix email typos (.con → .com) and complete domains from website column', category: 'format', example: { before: 'john@gmal.con', after: 'john@gmail.com' } },
  extract_name_from_email: { name: 'Extract Name from Email', icon: <Mail className="w-4 h-4" />, description: 'Guess First & Last Name from email address', category: 'extract', example: { before: 'john.doe@company.com', after: 'First: John, Last: Doe' } },
  extract_domain: { name: 'Extract Domain', icon: <Mail className="w-4 h-4" />, description: 'Get domain from email', category: 'extract', example: { before: 'john@acme.com', after: 'acme.com' } },
  // Phone
  phone_format: { name: 'Phone Format', icon: <Phone className="w-4 h-4" />, description: 'Auto-detect country code and standardize format', category: 'format', hasConfig: true, example: { before: '(555) 123-4567', after: '+15551234567' } },
  // Website
  website_clean: { name: 'Clean Website', icon: <Globe className="w-4 h-4" />, description: 'Standardize to www.domain.com', category: 'format', example: { before: 'https://acme.com/page', after: 'www.acme.com' } },
  extract_website_domain: { name: 'Extract Website Domain', icon: <LinkIcon className="w-4 h-4" />, description: 'Get domain name from URL', category: 'extract', example: { before: 'https://www.acme.com/page', after: 'acme.com' } },
  // Location
  standardize_state: { name: 'Standardize State', icon: <MapPin className="w-4 h-4" />, description: 'Normalize US/Canadian state names', category: 'standardize', hasConfig: true, requiresValidation: true, example: { before: 'CA / calif / California', after: 'California' } },
  standardize_country: { name: 'Standardize Country', icon: <MapPin className="w-4 h-4" />, description: 'Normalize country names', category: 'standardize', example: { before: 'US / USA / United States', after: 'United States' } },
  postal_format: { name: 'Postal Code Format', icon: <MapPin className="w-4 h-4" />, description: 'Auto-detect and split ZIP+4 codes', category: 'format', hasConfig: true, example: { before: '12345-6789', after: '12345' } },
  // Date & Boolean
  date_format: { name: 'Date Format', icon: <Calendar className="w-4 h-4" />, description: 'Standardize dates', category: 'format', hasConfig: true, requiresValidation: true, example: { before: '01/15/2025', after: '2025-01-15' } },
  boolean_standardize: { name: 'Standardize Single Checkbox', icon: <Check className="w-4 h-4" />, description: 'Normalize Yes/No values', category: 'standardize', example: { before: 'Y / yes / TRUE', after: 'Yes' } },
  // Dropdown & Multi-select
  dropdown_standardize: { name: 'Dropdown Standardize', icon: <List className="w-4 h-4" />, description: 'View, rename, merge, delete values', category: 'custom', hasConfig: true, requiresValidation: true, example: { before: 'Various values', after: 'Standardized' } },
  multi_checkbox_standardize: { name: 'Multi-Checkbox Standardize', icon: <List className="w-4 h-4" />, description: 'Standardize values separated by semicolon (;)', category: 'custom', hasConfig: true, requiresValidation: true, example: { before: 'A;B;C', after: 'A; B; C' } },
  // Numbers & Revenue
  number_format: { name: 'Number Format', icon: <Hash className="w-4 h-4" />, description: 'Clean numeric values', category: 'format', example: { before: '$1,234.56', after: '1234.56' } },
  revenue_normalize: { name: 'Revenue Normalize', icon: <DollarSign className="w-4 h-4" />, description: 'Convert text (1 million, 10k) to numbers', category: 'format', example: { before: '1.5M / 10k', after: '1500000 / 10000' } },
  revenue_ranges: { name: 'Revenue Ranges', icon: <DollarSign className="w-4 h-4" />, description: 'Convert to revenue ranges', category: 'custom', hasConfig: true, requiresValidation: true, example: { before: '1500000', after: '$1M-$5M' } },
  // Cleanup
  remove_special_chars: { name: 'Remove Special Chars', icon: <Type className="w-4 h-4" />, description: 'Remove special characters', category: 'text', example: { before: 'John@#$Doe!', after: 'JohnDoe' } },
};

const DATE_FORMATS = [
  { value: '%Y-%m-%d', label: 'YYYY-MM-DD', example: '2025-01-11' },
  { value: '%m/%d/%Y', label: 'MM/DD/YYYY', example: '01/11/2025' },
  { value: '%d/%m/%Y', label: 'DD/MM/YYYY', example: '11/01/2025' },
  { value: '%d-%m-%Y', label: 'DD-MM-YYYY', example: '11-01-2025' },
  { value: '%B %d, %Y', label: 'Month DD, YYYY', example: 'January 11, 2025' },
  { value: '%d %B %Y', label: 'DD Month YYYY', example: '11 January 2025' },
];

const CleaningPage = () => {
  const { fileId, step: urlStep } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Mobile detection - cleaning tool is desktop-only
  const isMobile = useIsMobile();
  
  // React Tour hook
  const { setIsOpen, setCurrentStep, setSteps, isOpen } = useTour();
  const { dontShowAgain, setDontShowAgain } = useTourPreferences();
  
  const [fileData, setFileData] = useState(location.state?.fileData || null);
  const [analysis, setAnalysis] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [rules, setRules] = useState({});
  const [ruleConfigs, setRuleConfigs] = useState({});
  const [preview, setPreview] = useState(null);
  const [cleaningResult, setCleaningResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  
  // Map URL step to internal step number
  const getStepFromUrl = (urlStep) => {
    switch (urlStep) {
      case 'config': return 1;
      case 'apply': return 2;
      case 'success': return 3;
      default: return 1;
    }
  };
  
  // Map internal step number to URL step
  const getUrlFromStep = (stepNum) => {
    switch (stepNum) {
      case 1: return 'config';
      case 2: return 'apply';
      case 3: return 'success';
      default: return 'config';
    }
  };
  
  const [step, setStep] = useState(urlStep ? getStepFromUrl(urlStep) : 1);
  
  // Track if we're restoring from session (to avoid redirect race condition)
  const [restoringSession, setRestoringSession] = useState(false);
  
  // T05: Guided Column Validation - moved up to avoid temporal dead zone
  const [columnsToClean, setColumnsToClean] = useState([]); // Columns user selected to clean (mandatory)
  
  // Function to update step and URL simultaneously
  const updateStep = useCallback((newStep) => {
    setStep(newStep);
    const urlStep = getUrlFromStep(newStep);
    navigate(`/clean/${urlStep}/${fileId}`, { replace: true });
  }, [navigate, fileId]);
  
  // Redirect to config step if no step in URL
  useEffect(() => {
    if (!urlStep) {
      updateStep(1);
    }
  }, [urlStep, updateStep]);
  
  // Sync step with URL when URL changes
  useEffect(() => {
    // Skip validation during session restoration
    if (restoringSession) return;
    
    if (urlStep) {
      const newStep = getStepFromUrl(urlStep);
      if (newStep !== step) {
        setStep(newStep);
        
        // Validate step access - redirect to step 1 if trying to access later steps without data
        if (newStep === 2 && !preview) {
          // Trying to access preview step without preview data
          updateStep(1);
          toast.error('Please configure your cleaning rules first.');
        } else if (newStep === 3 && !cleaningResult) {
          // Trying to access success step - try to fetch the last job result
          const fetchLastJob = async () => {
            try {
              const response = await api.get(`/api/cleaning/last-job/${fileId}`);
              if (response.data && response.data.status === 'completed') {
                setCleaningResult(response.data);
                // Also restore rules from job if available
                if (response.data.rules) {
                  const restoredRules = {};
                  response.data.rules.forEach(rule => {
                    if (rule.column) {
                      if (!restoredRules[rule.column]) {
                        restoredRules[rule.column] = [];
                      }
                      restoredRules[rule.column].push(rule.rule_type);
                    }
                  });
                  setRules(restoredRules);
                }
              } else {
                updateStep(1);
                toast.error('Please complete the cleaning process first.');
              }
            } catch (error) {
              console.log('No previous cleaning job found');
              updateStep(1);
              toast.error('Please complete the cleaning process first.');
            }
          };
          fetchLastJob();
        }
      }
    }
  }, [urlStep, updateStep, preview, cleaningResult, step, restoringSession, fileId]);
  
  // Helper function to save cleaning session with full state
  const saveCleaningSession = useCallback(async () => {
    console.log('[CleaningPage] Saving session with rules:', rules, 'ruleConfigs:', ruleConfigs);
    return await api.post('/api/cleaning/save-session', {
      file_id: fileId,
      rules: rules,
      rule_configs: ruleConfigs,
      step: step,
      preview_data: preview,
      columns_to_clean: columnsToClean
    });
  }, [fileId, rules, ruleConfigs, step, preview, columnsToClean]);
  
  // Presets data
  const [presets, setPresets] = useState(null);
  
  // New column dialog
  const [newColumnDialogOpen, setNewColumnDialogOpen] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [newColumnSource, setNewColumnSource] = useState('');
  const [newColumnTransform, setNewColumnTransform] = useState('copy');
  
  // Manual mapping dialog
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [mappingColumn, setMappingColumn] = useState('');
  const [mappings, setMappings] = useState({});
  const [mappingOptions, setMappingOptions] = useState([]);
  const [newMappingOption, setNewMappingOption] = useState('');
  const [pasteOptions, setPasteOptions] = useState('');
  
  // Revenue ranges dialog
  const [revenueDialogOpen, setRevenueDialogOpen] = useState(false);
  const [revenueColumn, setRevenueColumn] = useState('');
  const [revenueSuggestions, setRevenueSuggestions] = useState(null);
  const [selectedRevenueRanges, setSelectedRevenueRanges] = useState([]);
  const [revenueTargetType, setRevenueTargetType] = useState(''); // 'smb' or 'enterprise'
  const [customRangeInput, setCustomRangeInput] = useState('');
  const [revenueCurrencyFallback, setRevenueCurrencyFallback] = useState('USD'); // Fallback currency
  
  // Revenue Normalize dialog state
  const [revenueNormalizeDialogOpen, setRevenueNormalizeDialogOpen] = useState(false);
  const [revenueNormalizeColumn, setRevenueNormalizeColumn] = useState(null);
  const [revenueNormalizeCurrency, setRevenueNormalizeCurrency] = useState('USD');
  
  // Cleaning error state (for file splitter CTA)
  const [cleaningError, setCleaningError] = useState(null);
  
  // Session time tracking (from page load to success)
  const [sessionStartTime] = useState(Date.now());
  const [sessionDuration, setSessionDuration] = useState(null);
  
  // Experience level (removed survey popup)
  const [experienceLevel, setExperienceLevel] = useState('beginner');
  
  // Get user info
  const { user, refreshUser } = useAuth();
  
  // Set experience level from user if available (no popup)
  useEffect(() => {
    if (user?.experience_level) {
      setExperienceLevel(user.experience_level);
    }
  }, [user]);
  
  // Industry preset dialog
  const [industryDialogOpen, setIndustryDialogOpen] = useState(false);
  const [selectedIndustryPreset, setSelectedIndustryPreset] = useState('');
  
  // Global settings
  const [globalStateFormat, setGlobalStateFormat] = useState('full'); // 'full' or 'abbrev'
  
  // State format confirmation dialog
  const [stateFormatDialogOpen, setStateFormatDialogOpen] = useState(false);
  const [pendingStateColumn, setPendingStateColumn] = useState('');
  
  // Dropdown standardization dialog
  const [dropdownDialogOpen, setDropdownDialogOpen] = useState(false);
  const [dropdownColumn, setDropdownColumn] = useState('');
  const [dropdownValues, setDropdownValues] = useState([]); // Array of {original, display, action: 'keep'|'rename'|'merge'|'delete', mergeTarget}
  const [editingValueIndex, setEditingValueIndex] = useState(null);
  const [editingNewValue, setEditingNewValue] = useState('');
  
  // Multi-checkbox standardization dialog
  const [multiCheckboxDialogOpen, setMultiCheckboxDialogOpen] = useState(false);
  const [multiCheckboxColumn, setMultiCheckboxColumn] = useState('');
  const [multiCheckboxValues, setMultiCheckboxValues] = useState([]); // Similar to dropdownValues but extracted from semicolon-separated values
  const [editingMultiValueIndex, setEditingMultiValueIndex] = useState(null);
  const [editingMultiNewValue, setEditingMultiNewValue] = useState('');
  
  // Feature guide modals (first-time tutorials)
  const [featureGuideOpen, setFeatureGuideOpen] = useState(null); // 'dropdown_standardize' | 'multi_checkbox' | 'revenue_ranges' | null
  
  // Template management
  const [templates, setTemplates] = useState([]);
  const [templateLimit, setTemplateLimit] = useState({ limit: 0, used: 0, can_create: false });
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [loadTemplateDialogOpen, setLoadTemplateDialogOpen] = useState(false);
  
  // T05: Guided Column Validation
  const [columnSelectionOpen, setColumnSelectionOpen] = useState(false);
  // columnsToClean is declared earlier to avoid temporal dead zone with saveCleaningSession
  const [validatedColumns, setValidatedColumns] = useState([]); // Columns user has validated (configured rules)
  const [guidedModeActive, setGuidedModeActive] = useState(true); // Whether to show guided flow
  const [currentColumnIndex, setCurrentColumnIndex] = useState(0); // Current position in guided flow
  
  // Show all rules toggle (default: only show suggested)
  const [showAllRules, setShowAllRules] = useState({});
  
  // Share dialog
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [downloadingImage, setDownloadingImage] = useState(false);
  const shareableStatsRef = useRef(null);
  
  // Cleaning product tour (manual dialog)
  const [showCleaningTour, setShowCleaningTour] = useState(false);
  const [cleaningTourStep, setCleaningTourStep] = useState(0);
  
  // Time Savings Modal
  const [showTimeSavingsModal, setShowTimeSavingsModal] = useState(false);
  
  // Download progress
  const [downloadProgress, setDownloadProgress] = useState({ show: false, progress: 0, format: '' });
  
  // T15: Check for resume session on load - only when arriving via direct URL
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  useEffect(() => {
    const checkForResumeSession = async () => {
      // Only check for resume on initial load, not on every step change
      if (initialLoadComplete) return;
      
      try {
        const response = await api.get(`/api/cleaning/resume-session/${fileId}`);
        if (response.data.found && response.data.rules && Object.keys(response.data.rules).length > 0) {
          // Set restoring flag to prevent redirect during state updates
          setRestoringSession(true);
          
          // Restore saved rules and state
          console.log('[CleaningPage] Restoring session with rules:', response.data.rules);
          setRules(response.data.rules);
          setColumnsToClean(response.data.columns_to_clean || Object.keys(response.data.rules));
          
          // Restore rule configs if available
          if (response.data.rule_configs) {
            setRuleConfigs(response.data.rule_configs);
          }
          
          // Restore step and preview data if available
          const savedStep = response.data.step || 1;
          if (savedStep > 1 && response.data.preview_data) {
            setPreview(response.data.preview_data);
          }
          
          // Small delay to let state updates settle before changing step
          setTimeout(() => {
            updateStep(savedStep);
            setRestoringSession(false);
            setInitialLoadComplete(true);
            toast.success('Session restored! Your cleaning rules have been loaded.');
          }, 100);
          
          // Mark session as resumed
          await api.post(`/api/cleaning/mark-session-resumed/${response.data.session_id}`);
        } else {
          // No session found - just mark initial load as complete
          // Don't redirect - user may be navigating normally
          setInitialLoadComplete(true);
        }
      } catch (error) {
        // Silently ignore - no saved session
        console.log('No saved session to resume:', error.message);
        setInitialLoadComplete(true);
      }
    };
    
    checkForResumeSession();
  }, [fileId]); // Only run on fileId change, not on step change
  
  // Track when the product tour closes to save completion status
  const [tourWasOpen, setTourWasOpen] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setTourWasOpen(true);
    } else if (tourWasOpen && !isOpen) {
      // Tour just closed
      handleProductTourComplete();
      setTourWasOpen(false);
    }
  }, [isOpen, tourWasOpen]);
  
  // Track previous fileId to detect actual file changes
  const prevFileIdRef = useRef(null);
  
  // Reset state when fileId changes (e.g., "Clean another file")
  useEffect(() => {
    // Skip on initial mount (let fetchAnalysis handle initial setup)
    if (prevFileIdRef.current === null) {
      prevFileIdRef.current = fileId;
      return;
    }
    
    // Only reset if fileId actually changed (not just on re-renders due to updateStep)
    if (prevFileIdRef.current === fileId) {
      return;
    }
    prevFileIdRef.current = fileId;
    
    // Reset all state for new file
    setFileData(null);
    setAnalysis(null);
    setSelectedColumn(null);
    setRules({});
    setRuleConfigs({});
    setPreview(null);
    setCleaningResult(null);
    setColumnsToClean([]);
    setValidatedColumns([]);
    setCurrentColumnIndex(0);
    updateStep(1);
    setColumnSelectionOpen(false);
    setCleaningError(null);
  }, [fileId, updateStep]);
  
  const fetchFileData = useCallback(async () => {
    try {
      const response = await api.get(`/api/files/${fileId}`);
      setFileData(response.data);
      
      // If we have columns_info, log which columns are too large
      if (response.data.columns_info) {
        const largeColumns = Object.entries(response.data.columns_info)
          .filter(([_, info]) => info.too_large_for_cleaning)
          .map(([col, _]) => col);
        if (largeColumns.length > 0) {
          console.log('Columns too large for cleaning:', largeColumns);
        }
      }
    } catch (error) {
      console.error('Failed to fetch file:', error);
      
      // Handle specific error types
      if (error.response?.status === 403 && error.response?.data?.detail?.upgrade_required) {
        const detail = error.response.data.detail;
        const fileRows = detail.file_rows || 0;
        const maxPerFile = detail.max_per_file || 0;
        const errorMsg = detail.message || `File has ${fileRows.toLocaleString()} rows, which is more than this file type supports.`;
        toast.error(errorMsg);
        setCleaningError({
          type: 'limit_reached',
          message: errorMsg,
          rowCount: fileRows,
          maxPerFile: maxPerFile
        });
      } else {
        // Ensure we extract string message properly
        let errorMsg = 'Failed to load file';
        if (error.response?.data?.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMsg = error.response.data.detail;
          } else if (error.response.data.detail.message) {
            errorMsg = error.response.data.detail.message;
          }
        }
        toast.error(errorMsg);
      }
      navigate('/dashboard');
    }
  }, [fileId, navigate]);

  const fetchAnalysis = useCallback(async () => {
    try {
      const response = await api.get(`/api/files/${fileId}/analyze`);
      setAnalysis(response.data);
      
      // Select first column that's not too large
      if (response.data.columns?.length > 0) {
        const firstValidColumn = response.data.columns.find(col => {
          const info = fileData?.columns_info?.[col.column_name];
          return !info?.too_large_for_cleaning;
        });
        if (firstValidColumn) {
          setSelectedColumn(firstValidColumn.column_name);
        } else {
          setSelectedColumn(response.data.columns[0].column_name);
        }
      }
      
      // Don't pre-select AI recommendations - just display them
      // User must click to enable each rule
      const initialRules = {};
      setRules(initialRules);
      
      // T05: Show column selection dialog after file loads - ONLY on config step (step 1)
      // Don't show on apply (step 2) or success (step 3) pages
      const currentUrlStep = window.location.pathname.split('/')[2]; // 'config', 'apply', or 'success'
      if (currentUrlStep === 'config' || !currentUrlStep) {
        setColumnSelectionOpen(true);
      }
    } catch (error) {
      console.error('Failed to analyze file:', error);
    } finally {
      setLoading(false);
    }
  }, [fileId]);

  const fetchPresets = useCallback(async () => {
    try {
      const response = await api.get('/api/presets');
      setPresets(response.data);
    } catch (error) {
      console.error('Failed to fetch presets:', error);
    }
  }, []);

  // Fetch data on mount - useEffect must come after function definitions
  useEffect(() => {
    fetchFileData();
    fetchAnalysis();
    fetchPresets();
  }, [fileId, fetchFileData, fetchAnalysis, fetchPresets]);

  // Template functions
  const fetchTemplates = async () => {
    try {
      const [templatesRes, limitRes] = await Promise.all([
        api.get('/api/templates'),
        api.get('/api/templates/limit')
      ]);
      setTemplates(templatesRes.data.templates || []);
      setTemplateLimit(limitRes.data);
    } catch (error) {
      console.log('Could not fetch templates');
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    const rulesArray = [];
    Object.entries(rules).forEach(([column, columnRules]) => {
      columnRules.forEach(ruleType => {
        const config = ruleConfigs[`${column}_${ruleType}`] || {};
        rulesArray.push({ column, rule_type: ruleType, config });
      });
    });
    
    if (rulesArray.length === 0) {
      toast.error('No rules to save. Please select some cleaning rules first.');
      return;
    }
    
    try {
      await api.post('/api/templates', {
        name: templateName,
        description: templateDescription,
        rules: rulesArray
      });
      toast.success('Template saved successfully!');
      setSaveTemplateDialogOpen(false);
      setTemplateName('');
      setTemplateDescription('');
      fetchTemplates();
    } catch (error) {
      if (error.response?.data?.detail?.upgrade_required) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error('Failed to save template');
      }
    }
  };

  const loadTemplate = (template) => {
    const newRules = {};
    const newConfigs = {};
    
    template.rules.forEach(rule => {
      const { column, rule_type, config } = rule;
      if (!newRules[column]) {
        newRules[column] = [];
      }
      newRules[column].push(rule_type);
      if (config && Object.keys(config).length > 0) {
        newConfigs[`${column}_${rule_type}`] = config;
      }
    });
    
    setRules(newRules);
    setRuleConfigs(newConfigs);
    setLoadTemplateDialogOpen(false);
    toast.success(`Template "${template.name}" loaded`);
  };

  const deleteTemplate = async (templateId) => {
    try {
      await api.delete(`/api/templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleRuleToggle = (column, rule, forceEnable = false) => {
    setRules(prev => {
      const columnRules = prev[column] || [];
      if (columnRules.includes(rule) && !forceEnable) {
        return {
          ...prev,
          [column]: columnRules.filter(r => r !== rule)
        };
      } else {
        if (rule === 'manual_mapping') {
          openMappingDialog(column);
        } else if (rule === 'revenue_ranges') {
          openRevenueDialog(column);
        } else if (rule === 'revenue_normalize' && !forceEnable) {
          openRevenueNormalizeDialog(column);
        } else if (rule === 'dropdown_standardize') {
          openDropdownDialog(column);
        } else if (rule === 'standardize_state') {
          // Schedule dialog to open after state update
          setTimeout(() => {
            setPendingStateColumn(column);
            setStateFormatDialogOpen(true);
          }, 0);
        }
        return {
          ...prev,
          [column]: [...columnRules, rule]
        };
      }
    });
  };

  const confirmStateFormat = (format) => {
    setGlobalStateFormat(format);
    setStateFormatDialogOpen(false);
    toast.success(`State names will be formatted as ${format === 'full' ? 'full names (e.g., California)' : 'abbreviations (e.g., CA)'}`);
  };

  const handleRuleConfig = (column, rule, configKey, configValue) => {
    setRuleConfigs(prev => ({
      ...prev,
      [`${column}_${rule}`]: {
        ...(prev[`${column}_${rule}`] || {}),
        [configKey]: configValue
      }
    }));
  };

  // Manual mapping functions
  const openMappingDialog = (column) => {
    setMappingColumn(column);
    const colAnalysis = getColumnAnalysis(column);
    const uniqueVals = colAnalysis?.unique_values || [];
    setMappingOptions([]);
    
    const initialMappings = {};
    uniqueVals.forEach(val => {
      initialMappings[val] = '';
    });
    setMappings(initialMappings);
    setMappingDialogOpen(true);
  };

  const addMappingOption = () => {
    if (newMappingOption.trim() && !mappingOptions.includes(newMappingOption.trim())) {
      setMappingOptions(prev => [...prev, newMappingOption.trim()]);
      setNewMappingOption('');
    }
  };

  const handlePasteOptions = () => {
    if (!pasteOptions.trim()) return;
    
    // Split by newlines, commas, or tabs
    const newOptions = pasteOptions
      .split(/[\n,\t]+/)
      .map(opt => opt.trim())
      .filter(opt => opt && !mappingOptions.includes(opt));
    
    setMappingOptions(prev => [...prev, ...newOptions]);
    setPasteOptions('');
    toast.success(`Added ${newOptions.length} options`);
  };

  const loadPresetOptions = (presetType) => {
    let options = [];
    
    if (presetType === 'us_states') {
      options = presets?.us_states || [];
    } else if (presetType === 'canadian_provinces') {
      options = presets?.canadian_provinces || [];
    } else if (presetType === 'all_states') {
      options = [...(presets?.us_states || []), ...(presets?.canadian_provinces || [])];
    } else if (presetType === 'countries') {
      options = presets?.countries || [];
    } else if (presets?.industry_presets?.[presetType]) {
      options = presets.industry_presets[presetType].values;
    }
    
    const newOptions = options.filter(opt => !mappingOptions.includes(opt));
    setMappingOptions(prev => [...prev, ...newOptions]);
    toast.success(`Loaded ${newOptions.length} options`);
  };

  const saveMappings = () => {
    const validMappings = {};
    Object.entries(mappings).forEach(([key, value]) => {
      // Skip placeholder value and empty values
      if (value && value !== '__keep_original__') {
        validMappings[key] = value;
      }
    });
    
    handleRuleConfig(mappingColumn, 'manual_mapping', 'mappings', validMappings);
    setMappingDialogOpen(false);
    toast.success('Mappings saved');
  };

  // Revenue ranges functions
  const SMB_RANGES = ['$0 - $1M', '$1M - $5M', '$5M - $10M', '$10M - $50M', '$50M+'];
  const ENTERPRISE_RANGES = ['$0 - $50M', '$50M - $100M', '$100M - $500M', '$500M - $1B', '$1B+'];

  const openRevenueDialog = async (column) => {
    // Show first-time guide if not seen before
    if (!hasSeenGuide('revenue_ranges')) {
      setFeatureGuideOpen('revenue_ranges');
      // Store column to open after guide closes
      setRevenueColumn(column);
      return;
    }
    
    _openRevenueDialogInternal(column);
  };
  
  const _openRevenueDialogInternal = async (column) => {
    setRevenueColumn(column);
    setRevenueTargetType(''); // Reset selection
    setSelectedRevenueRanges([]);
    setCustomRangeInput('');
    setRevenueDialogOpen(true);
    
    try {
      const response = await api.post(
        `/api/presets/revenue-ranges?file_id=${fileId}&column=${column}`,
        {}
      );
      setRevenueSuggestions(response.data);
    } catch (error) {
      console.error('Failed to get revenue suggestions:', error);
      toast.error('Failed to analyze revenue data');
    }
  };

  const selectRevenueTargetType = (type) => {
    setRevenueTargetType(type);
    // Pre-select the appropriate ranges based on target
    if (type === 'smb') {
      setSelectedRevenueRanges([...SMB_RANGES]);
    } else if (type === 'enterprise') {
      setSelectedRevenueRanges([...ENTERPRISE_RANGES]);
    }
  };

  const addCustomRange = () => {
    if (customRangeInput.trim() && !selectedRevenueRanges.includes(customRangeInput.trim())) {
      setSelectedRevenueRanges(prev => [...prev, customRangeInput.trim()]);
      setCustomRangeInput('');
    }
  };

  const addSuggestedRange = (range) => {
    if (!selectedRevenueRanges.includes(range)) {
      setSelectedRevenueRanges(prev => [...prev, range]);
    }
  };

  const removeRange = (range) => {
    setSelectedRevenueRanges(prev => prev.filter(r => r !== range));
  };

  const saveRevenueRanges = () => {
    // Validation: Must have target type selected
    if (!revenueTargetType) {
      toast.error('Please select your target customer type (SMB or Enterprise)');
      return;
    }
    
    // Validation: Must have at least 2 ranges selected
    if (selectedRevenueRanges.length < 2) {
      toast.error('Please select at least 2 revenue ranges to continue');
      return;
    }
    
    const rangeMappings = {};
    // Create mappings from revenue values to ranges
    // This will be handled in the backend with the manual_mapping rule
    handleRuleConfig(revenueColumn, 'manual_mapping', 'mappings', rangeMappings);
    handleRuleConfig(revenueColumn, 'revenue_ranges', 'ranges', selectedRevenueRanges);
    handleRuleConfig(revenueColumn, 'revenue_ranges', 'fallback_currency', revenueCurrencyFallback);
    setRevenueDialogOpen(false);
    toast.success('Revenue ranges configured');
  };

  // Revenue Normalize dialog functions
  const openRevenueNormalizeDialog = (column) => {
    setRevenueNormalizeColumn(column);
    setRevenueNormalizeCurrency('USD');
    setRevenueNormalizeDialogOpen(true);
  };

  const saveRevenueNormalize = () => {
    if (!revenueNormalizeColumn) return;
    
    // Enable the rule
    handleRuleToggle(revenueNormalizeColumn, 'revenue_normalize', true);
    // Set the fallback currency config
    handleRuleConfig(revenueNormalizeColumn, 'revenue_normalize', 'fallback_currency', revenueNormalizeCurrency);
    
    setRevenueNormalizeDialogOpen(false);
    toast.success('Revenue normalize configured');
  };

  // Dropdown standardization functions
  const openDropdownDialog = (column) => {
    // Show first-time guide if not seen before
    if (!hasSeenGuide('dropdown_standardize')) {
      setFeatureGuideOpen('dropdown_standardize');
      // Store column to open after guide closes
      setDropdownColumn(column);
      return;
    }
    
    _openDropdownDialogInternal(column);
  };
  
  const _openDropdownDialogInternal = (column) => {
    setDropdownColumn(column);
    const colAnalysis = getColumnAnalysis(column);
    const uniqueVals = colAnalysis?.unique_values || [];
    
    // Sort values alphabetically A-Z
    const sortedVals = [...uniqueVals].sort((a, b) => {
      if (!a) return 1;
      if (!b) return -1;
      return String(a).localeCompare(String(b));
    });
    
    // Initialize values with counts
    const valuesWithActions = sortedVals.map(val => ({
      original: val,
      display: val,
      action: 'keep', // 'keep', 'rename', 'merge', 'delete'
      mergeTarget: null,
      count: fileData?.sample_data?.filter(row => row[column] === val).length || 0
    }));
    
    setDropdownValues(valuesWithActions);
    setDropdownDialogOpen(true);
  };

  const handleDropdownRename = (index, newValue) => {
    setDropdownValues(prev => prev.map((item, i) => 
      i === index ? { ...item, display: newValue, action: 'rename' } : item
    ));
    setEditingValueIndex(null);
    setEditingNewValue('');
  };

  const handleDropdownMerge = (sourceIndex, targetValue) => {
    setDropdownValues(prev => prev.map((item, i) => 
      i === sourceIndex ? { ...item, action: 'merge', mergeTarget: targetValue, display: targetValue } : item
    ));
  };

  const handleDropdownDelete = (index) => {
    setDropdownValues(prev => prev.map((item, i) => 
      i === index ? { ...item, action: 'delete' } : item
    ));
  };

  const handleDropdownRestore = (index) => {
    setDropdownValues(prev => prev.map((item, i) => 
      i === index ? { ...item, action: 'keep', display: item.original, mergeTarget: null } : item
    ));
  };

  const saveDropdownStandardization = () => {
    // Convert dropdown actions to manual_mapping config
    const mappings = {};
    dropdownValues.forEach(item => {
      if (item.action === 'rename' && item.display !== item.original) {
        mappings[item.original] = item.display;
      } else if (item.action === 'merge' && item.mergeTarget) {
        mappings[item.original] = item.mergeTarget;
      } else if (item.action === 'delete') {
        mappings[item.original] = ''; // Empty string means delete
      }
    });
    
    handleRuleConfig(dropdownColumn, 'dropdown_standardize', 'mappings', mappings);
    setDropdownDialogOpen(false);
    toast.success('Dropdown standardization saved');
  };

  // Multi-checkbox standardization functions (for semicolon-separated values)
  const openMultiCheckboxDialog = (column) => {
    // Show first-time guide if not seen before
    if (!hasSeenGuide('multi_checkbox')) {
      setFeatureGuideOpen('multi_checkbox');
      // Store column to open after guide closes
      setMultiCheckboxColumn(column);
      return;
    }
    
    _openMultiCheckboxDialogInternal(column);
  };
  
  const _openMultiCheckboxDialogInternal = (column) => {
    setMultiCheckboxColumn(column);
    const colAnalysis = getColumnAnalysis(column);
    
    // Extract all unique values from semicolon-separated cells
    const allValues = new Set();
    const sampleData = fileData?.sample_data || [];
    sampleData.forEach(row => {
      const cellValue = row[column];
      if (cellValue && typeof cellValue === 'string') {
        cellValue.split(';').forEach(v => {
          const trimmed = v.trim();
          if (trimmed) allValues.add(trimmed);
        });
      }
    });
    
    // Also include unique values from analysis if available
    (colAnalysis?.unique_values || []).forEach(val => {
      if (val && typeof val === 'string') {
        val.split(';').forEach(v => {
          const trimmed = v.trim();
          if (trimmed) allValues.add(trimmed);
        });
      }
    });
    
    // Sort alphabetically A-Z
    const sortedValues = Array.from(allValues).sort((a, b) => a.localeCompare(b));
    
    const valuesWithActions = sortedValues.map(val => ({
      original: val,
      display: val,
      action: 'keep',
      mergeTarget: null
    }));
    
    setMultiCheckboxValues(valuesWithActions);
    setMultiCheckboxDialogOpen(true);
  };

  const handleMultiCheckboxRename = (index, newValue) => {
    setMultiCheckboxValues(prev => prev.map((item, i) => 
      i === index ? { ...item, display: newValue, action: 'rename' } : item
    ));
    setEditingMultiValueIndex(null);
    setEditingMultiNewValue('');
  };

  const handleMultiCheckboxMerge = (sourceIndex, targetValue) => {
    setMultiCheckboxValues(prev => prev.map((item, i) => 
      i === sourceIndex ? { ...item, action: 'merge', mergeTarget: targetValue, display: targetValue } : item
    ));
  };

  const handleMultiCheckboxDelete = (index) => {
    setMultiCheckboxValues(prev => prev.map((item, i) => 
      i === index ? { ...item, action: 'delete' } : item
    ));
  };

  const handleMultiCheckboxRestore = (index) => {
    setMultiCheckboxValues(prev => prev.map((item, i) => 
      i === index ? { ...item, action: 'keep', display: item.original, mergeTarget: null } : item
    ));
  };

  const saveMultiCheckboxStandardization = () => {
    const mappings = {};
    multiCheckboxValues.forEach(item => {
      if (item.action === 'rename' && item.display !== item.original) {
        mappings[item.original] = item.display;
      } else if (item.action === 'merge' && item.mergeTarget) {
        mappings[item.original] = item.mergeTarget;
      } else if (item.action === 'delete') {
        mappings[item.original] = '';
      }
    });
    
    handleRuleConfig(multiCheckboxColumn, 'multi_checkbox_standardize', 'mappings', mappings);
    setMultiCheckboxDialogOpen(false);
    toast.success('Multi-checkbox standardization saved');
  };

  // New column functions
  const handleAddNewColumn = () => {
    if (!newColumnName.trim() || !newColumnSource) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setRules(prev => ({
      ...prev,
      ['__new_columns__']: [...(prev['__new_columns__'] || []), 'create_column']
    }));
    
    const configKey = `create_column_${newColumnName}`;
    setRuleConfigs(prev => ({
      ...prev,
      [configKey]: {
        source_column: newColumnSource,
        new_column_name: newColumnName,
        transform: newColumnTransform
      }
    }));
    
    if (fileData) {
      setFileData(prev => ({
        ...prev,
        columns: [...(prev.columns || []), newColumnName]
      }));
    }
    
    setNewColumnDialogOpen(false);
    setNewColumnName('');
    setNewColumnSource('');
    setNewColumnTransform('copy');
    toast.success(`Column "${newColumnName}" will be created`);
  };

  const handlePreview = async () => {
    const rulesArray = [];
    
    Object.entries(rules).forEach(([column, columnRules]) => {
      if (column === '__new_columns__') return;
      
      columnRules.forEach(rule_type => {
        // Handle revenue_ranges - creates a new column
        if (rule_type === 'revenue_ranges') {
          const config = ruleConfigs[`${column}_${rule_type}`] || {};
          if (config.ranges && config.ranges.length > 0) {
            rulesArray.push({ column, rule_type: 'revenue_ranges', config });
          }
          return;
        }
        
        let config = ruleConfigs[`${column}_${rule_type}`] || {};
        
        // Apply global state format setting
        if (rule_type === 'standardize_state') {
          config = { ...config, output_format: globalStateFormat };
        }
        
        // Convert dropdown_standardize to manual_mapping
        if (rule_type === 'dropdown_standardize') {
          rulesArray.push({ column, rule_type: 'manual_mapping', config });
          return;
        }
        
        rulesArray.push({ column, rule_type, config });
      });
    });
    
    Object.entries(ruleConfigs).forEach(([key, config]) => {
      if (key.startsWith('create_column_')) {
        rulesArray.push({
          column: config.source_column,
          rule_type: 'create_column',
          config
        });
      }
    });
    
    if (rulesArray.length === 0) {
      toast.error('Please select at least one cleaning rule');
      return;
    }
    
    setPreviewLoading(true);
    
    try {
      const response = await api.post(
        `/api/cleaning/preview?file_id=${fileId}`,
        rulesArray
      );
      
      setPreview(response.data);
      
      // Save session with rules so they persist through navigation/refresh
      console.log('[CleaningPage] Saving session after preview with rules:', rules);
      await saveCleaningSession();
      
      updateStep(2);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Cleaning is free and unlimited - proceed directly, no credit/subscription gate
  const handleCleanWithConfirmation = async () => {
    handleClean();
  };

  const handleClean = async () => {
    // DEBUG: Log the current rules state
    console.log('[CleaningPage] handleClean starting');
    console.log('[CleaningPage] Current rules state:', JSON.stringify(rules));
    console.log('[CleaningPage] Current ruleConfigs state:', JSON.stringify(ruleConfigs));
    
    let rulesArray = [];
    let effectiveRules = rules;
    let effectiveRuleConfigs = ruleConfigs;
    
    // If rules state is empty, try to recover from session
    if (Object.keys(rules).length === 0 || Object.keys(rules).filter(k => k !== '__new_columns__').length === 0) {
      console.log('[CleaningPage] Rules state is empty, trying to recover from session...');
      try {
        const sessionResponse = await api.get(`/api/cleaning/resume-session/${fileId}`);
        if (sessionResponse.data.found && sessionResponse.data.rules) {
          effectiveRules = sessionResponse.data.rules;
          effectiveRuleConfigs = sessionResponse.data.rule_configs || {};
          console.log('[CleaningPage] Recovered rules from session:', effectiveRules);
          console.log('[CleaningPage] Recovered ruleConfigs from session:', effectiveRuleConfigs);
          
          // Also restore to state for future use
          setRules(effectiveRules);
          setRuleConfigs(effectiveRuleConfigs);
        }
      } catch (error) {
        console.error('[CleaningPage] Failed to recover session:', error);
      }
    }
    
    // Build rules array from effective rules
    Object.entries(effectiveRules).forEach(([column, columnRules]) => {
      if (column === '__new_columns__') return;
      
      console.log(`[CleaningPage] Processing column: ${column} with rules:`, columnRules);
      
      columnRules.forEach(rule_type => {
        // Handle revenue_ranges - creates a new column
        if (rule_type === 'revenue_ranges') {
          const config = effectiveRuleConfigs[`${column}_${rule_type}`] || {};
          if (config.ranges && config.ranges.length > 0) {
            rulesArray.push({ column, rule_type: 'revenue_ranges', config });
          }
          return;
        }
        
        let config = effectiveRuleConfigs[`${column}_${rule_type}`] || {};
        
        // Apply global state format setting
        if (rule_type === 'standardize_state') {
          config = { ...config, output_format: globalStateFormat };
        }
        
        // Convert dropdown_standardize to manual_mapping
        if (rule_type === 'dropdown_standardize') {
          rulesArray.push({ column, rule_type: 'manual_mapping', config });
          return;
        }
        
        rulesArray.push({ column, rule_type, config });
      });
    });
    
    // Also handle create_column rules from ruleConfigs
    Object.entries(effectiveRuleConfigs).forEach(([key, config]) => {
      if (key.startsWith('create_column_')) {
        rulesArray.push({
          column: config.source_column,
          rule_type: 'create_column',
          config: {
            new_column_name: config.new_column_name,
            transform: config.transform
          }
        });
      }
    });
    
    // DEBUG: Log rules being sent to backend
    console.log('[CleaningPage] handleClean rulesArray:', rulesArray);
    console.log('[CleaningPage] handleClean rules state:', rules);
    console.log('[CleaningPage] handleClean rulesArray length:', rulesArray.length);
    
    // Note: Don't block here - let backend handle empty rules case
    // The backend will return an error if needed
    
    setCleaning(true);
    
    // Track start time for elapsed time calculation
    const cleaningStartTime = Date.now();
    
    // Calculate timeout based on file size
    const rowCount = fileData?.row_count || 0;
    const timeoutMs = Math.max(120000, rowCount > 50000 ? 300000 : 120000); // 5 min for large files
    
    // Show progress message for large files
    if (rowCount > 50000) {
      toast.info(`Processing ${rowCount.toLocaleString()} rows. This may take a few moments...`, { duration: 10000 });
    }
    
    try {
      const response = await api.post(`/api/cleaning/apply`, {
        file_id: fileId,
        rules: rulesArray,
        experience_level: experienceLevel
      }, {
        timeout: timeoutMs
      });
      
      // Calculate elapsed time and add it to the result
      const elapsedSeconds = (Date.now() - cleaningStartTime) / 1000;
      const resultWithElapsedTime = {
        ...response.data,
        elapsed_time_seconds: Math.round(elapsedSeconds * 100) / 100
      };
      
      setCleaningResult(resultWithElapsedTime);
      
      // Clear any previous cleaning errors on success
      setCleaningError(null);
      
      // Calculate session duration (time from page load to success)
      const durationMs = Date.now() - sessionStartTime;
      setSessionDuration(durationMs);
      
      updateStep(3);
      toast.success(`Data cleaned successfully! ${response.data.cleaned_row_count.toLocaleString()} rows processed.`);
    } catch (error) {
      console.error('Cleaning error:', error);
      
      const statusCode = error.response?.status;
      
      // Handle timeout for large files
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        const errorMsg = `Cleaning timed out for ${rowCount.toLocaleString()} rows. Try splitting your file into smaller parts.`;
        toast.error(errorMsg);
        setCleaningError({
          type: 'timeout',
          message: errorMsg,
          rowCount: rowCount,
          userFriendlyTitle: 'Processing Timeout',
          userFriendlyMessage: 'Your file is too large to process in one go. Please split it into smaller files (under 50,000 rows each) and clean them separately.',
          actionText: 'Try with a smaller file'
        });
      }
      // Handle 520 errors (server/infrastructure errors)
      else if (statusCode === 520 || statusCode === 502 || statusCode === 503 || statusCode === 504) {
        const errorMsg = `Server temporarily unavailable (Error ${statusCode}). This can happen with very large files.`;
        toast.error(errorMsg, { duration: 8000 });
        setCleaningError({
          type: 'server_error',
          message: errorMsg,
          rowCount: rowCount,
          userFriendlyTitle: 'Server Busy',
          userFriendlyMessage: `Our servers are temporarily overloaded. This often happens with large files (${rowCount.toLocaleString()} rows). Please wait a moment and try again, or split your file into smaller parts.`,
          actionText: 'Retry Cleaning',
          canRetry: true
        });
      }
      // Handle columns too large error
      else if (statusCode === 400 && error.response?.data?.detail?.error === 'columns_too_large') {
        const detail = error.response.data.detail;
        toast.error(detail.message, { duration: 8000 });
        setCleaningError({
          type: 'columns_too_large',
          message: detail.message,
          rowCount: rowCount,
          userFriendlyTitle: 'Column Data Too Large',
          userFriendlyMessage: 'Some columns contain very long text values that cannot be processed. Try removing columns with large text content (like descriptions or notes) from cleaning.',
          actionText: 'Select different columns'
        });
      }
      else {
        // Show actual error message from backend - ensure it's a string
        let errorDetail = error.response?.data?.detail?.message 
          || (typeof error.response?.data?.detail === 'string' ? error.response?.data?.detail : null)
          || error.message 
          || 'Unknown error';
        
        // Handle case where detail is an object
        if (typeof error.response?.data?.detail === 'object' && error.response?.data?.detail !== null) {
          errorDetail = error.response.data.detail.message || JSON.stringify(error.response.data.detail);
        }
        
        toast.error(`Cleaning failed: ${errorDetail}`);
        setCleaningError({
          type: 'general',
          message: errorDetail,
          rowCount: rowCount,
          userFriendlyTitle: 'Cleaning Failed',
          userFriendlyMessage: `Something went wrong while cleaning your file. ${errorDetail}`,
          actionText: 'Try Again',
          canRetry: true
        });
      }
    } finally {
      setCleaning(false);
    }
  };

  const handleExport = async (format) => {
    if (!cleaningResult?.job_id) return;
    
    try {
      const isLargeFile = cleaningResult?.cleaned_row_count > 30000;
      const actualFormat = isLargeFile && format !== 'zip' ? 'zip' : format;
      
      // Show download progress dialog
      setDownloadProgress({ show: true, progress: 10, format: actualFormat });
      
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setDownloadProgress(prev => ({
          ...prev,
          progress: Math.min(prev.progress + Math.random() * 15, 85)
        }));
      }, 500);
      
      const response = await api.post('/api/export', {
        job_id: cleaningResult.job_id,
        format: actualFormat
      }, {
        responseType: 'blob',
        timeout: 300000  // 5 minute timeout for large files
      });
      
      clearInterval(progressInterval);
      setDownloadProgress(prev => ({ ...prev, progress: 100 }));
      
      // Check if response is an error (JSON) instead of a file (blob)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const error = JSON.parse(text);
        throw new Error(error.detail || 'Export failed');
      }
      
      // Get original filename without extension and create new name
      const originalName = fileData?.original_name || 'data';
      const baseName = originalName.replace(/\.(csv|xlsx|xls)$/i, '');
      const filename = actualFormat === 'zip' 
        ? `${baseName}_cleaned.zip` 
        : `${baseName}_cleaned.${actualFormat}`;
      downloadFromResponse(response.data, filename, actualFormat);
      
      // Hide progress after short delay
      setTimeout(() => {
        setDownloadProgress({ show: false, progress: 0, format: '' });
      }, 500);
      
      toast.success(isLargeFile 
        ? 'Downloaded as ZIP! Check README.txt inside for instructions.' 
        : `Downloaded as ${actualFormat.toUpperCase()}`
      );
    } catch (error) {
      setDownloadProgress({ show: false, progress: 0, format: '' });
      console.error('Export error:', error);
      if (error.response?.status === 403) {
        toast.error('Export failed. Please try again.');
      } else {
        const errorDetail = error.response?.data?.detail || error.message || 'Unknown error';
        toast.error(`Export failed: ${errorDetail}`);
      }
    }
  };

  const getColumnAnalysis = (columnName) => {
    return analysis?.columns?.find(c => c.column_name === columnName);
  };

  const getTotalRulesCount = () => {
    let count = Object.values(rules).flat().filter(r => r !== 'create_column' && r !== 'revenue_ranges').length;
    Object.keys(ruleConfigs).forEach(key => {
      if (key.startsWith('create_column_')) count++;
    });
    return count;
  };

  // T05: Guided Validation Helper Functions
  const handleColumnSelectionConfirm = () => {
    if (columnsToClean.length === 0) {
      toast.error('Please select at least one column to clean');
      return;
    }
    setColumnSelectionOpen(false);
    // Auto-select the first column to clean
    setSelectedColumn(columnsToClean[0]);
    setCurrentColumnIndex(0);
    setGuidedModeActive(true);
    
    // Auto-trigger product tour ONLY for first-time cleaners who haven't completed the tour
    // Check both product_tour_completed AND if they've cleaned files before
    const hasCleanedBefore = user?.aggregate_metrics?.total_files_cleaned > 0;
    if (user && !user.product_tour_completed && !hasCleanedBefore) {
      // Small delay to let the UI render before starting tour
      setTimeout(() => {
        startProductTour(true);
      }, 500);
    }
  };

  const handleSkipColumnSelection = () => {
    // User wants to skip guided mode - let them use free-form
    setColumnSelectionOpen(false);
    setGuidedModeActive(false);
    setColumnsToClean([]);
    // Select first column by default
    if (fileData?.columns?.length > 0) {
      setSelectedColumn(fileData.columns[0]);
    }
    
    // Also trigger tour for free-form mode users who are first-time cleaners
    const hasCleanedBefore = user?.aggregate_metrics?.total_files_cleaned > 0;
    if (user && !user.product_tour_completed && !hasCleanedBefore) {
      setTimeout(() => {
        startProductTour(false);
      }, 500);
    }
  };
  
  // Function to start the product tour
  const startProductTour = (isGuided = true) => {
    // Reset the dontShowAgain checkbox when starting a new tour
    setDontShowAgain(false);
    const hasAISuggestions = analysis?.columns?.some(col => col.suggested_rules?.length > 0);
    const steps = getTourSteps(hasAISuggestions, isGuided);
    setSteps(steps);
    setCurrentStep(0);
    setIsOpen(true);
  };

  const toggleColumnToClean = (col) => {
    setColumnsToClean(prev => 
      prev.includes(col) 
        ? prev.filter(c => c !== col)
        : [...prev, col]
    );
  };

  // Handle product tour completion or dismissal
  const handleProductTourComplete = async () => {
    setIsOpen(false);
    
    // Only save completion if user checked "Don't show again" or completed all steps
    if (dontShowAgain) {
      toast.success('Got it! Tour won\'t show again.');
      
      // Mark tour as completed in the backend
      try {
        await api.post('/api/auth/product-tour-completed');
        if (refreshUser) {
          refreshUser();
        }
      } catch (error) {
        console.log('Could not save tour completion status');
      }
    } else {
      toast.success('Tour closed. Click the help button (?) to see it again anytime!');
    }
    
    // Reset the checkbox
    setDontShowAgain(false);
  };

  const handleValidateColumn = () => {
    if (!selectedColumn) return;
    
    // Mark current column as validated
    if (!validatedColumns.includes(selectedColumn)) {
      setValidatedColumns(prev => [...prev, selectedColumn]);
    }
    
    // Move to next column if in guided mode
    if (guidedModeActive && columnsToClean.length > 0) {
      const nextIndex = currentColumnIndex + 1;
      if (nextIndex < columnsToClean.length) {
        setCurrentColumnIndex(nextIndex);
        setSelectedColumn(columnsToClean[nextIndex]);
        toast.success(`Column "${selectedColumn}" validated! Moving to next column.`);
      } else {
        toast.success('All columns validated! You can now preview changes.');
      }
    }
  };

  const isAllColumnsValidated = () => {
    if (!guidedModeActive || columnsToClean.length === 0) {
      // Not in guided mode - allow preview anytime
      return true;
    }
    // In guided mode - check if all selected columns have rules
    // AND check if rules requiring validation have been configured
    return columnsToClean.every(col => {
      if (!rules[col]?.length) return false;
      
      // Check if any rule requires validation and hasn't been configured
      for (const ruleKey of rules[col]) {
        const ruleInfo = RULE_INFO[ruleKey];
        if (ruleInfo?.requiresValidation) {
          // Check if there's a config for this rule
          const configKey = `${col}_${ruleKey}`;
          if (!ruleConfigs[configKey]) {
            // Rule requires validation but hasn't been configured
            // For dropdown_standardize and multi_checkbox_standardize, check if dialog was opened
            if (ruleKey === 'dropdown_standardize' || ruleKey === 'multi_checkbox_standardize') {
              // These are OK if user has rules, since they need to click "Manage Values"
              continue;
            }
          }
        }
      }
      return true;
    });
  };

  const getValidationProgress = () => {
    if (columnsToClean.length === 0) return 100;
    const configured = columnsToClean.filter(col => rules[col]?.length > 0).length;
    return Math.round((configured / columnsToClean.length) * 100);
  };

  const getRemainingColumns = () => {
    return columnsToClean.filter(col => !rules[col]?.length);
  };

  // Mobile block - redirect users to desktop for cleaning
  if (isMobile) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-md text-center shadow-soft" data-testid="mobile-cleaning-blocked">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center bg-indigo-100 text-indigo-600">
            <Monitor className="w-10 h-10" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 mb-3">
            Desktop Required
          </h1>
          <p className="text-slate-600 mb-6">
            Our data cleaning tool is built for desktop screens to give you the best experience with data tables and column configuration.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Please open this link on your computer to continue cleaning your file.
          </p>
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium">
              <Monitor className="w-4 h-4" />
              Open on desktop to continue
            </div>
            <div className="pt-2">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600">Analyzing your data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
                <FileSpreadsheet className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-heading font-bold text-slate-900">CRM Data Cleaner</span>
            </Link>
            
            {/* Progress Steps */}
            <div className="flex items-center gap-4">
              {[
                { num: 1, label: 'Select Rules' },
                { num: 2, label: 'Preview' },
                { num: 3, label: 'Export' }
              ].map((s, i) => (
                <div key={s.num} className="flex items-center gap-2">
                  <div className={`progress-step-circle ${
                    step > s.num ? 'completed' : step === s.num ? 'active' : 'pending'
                  }`}>
                    {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                  </div>
                  <span className={`hidden sm:inline text-sm font-medium ${
                    step >= s.num ? 'text-slate-900' : 'text-slate-400'
                  }`}>
                    {s.label}
                  </span>
                  {i < 2 && <div className="w-8 h-px bg-slate-300 hidden sm:block"></div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Button 
          variant="ghost" 
          onClick={() => step > 1 ? updateStep(step - 1) : navigate('/dashboard')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {step > 1 ? 'Back' : 'Back to Dashboard'}
        </Button>

        {/* Step 1: Select Rules */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Template & Settings Panel */}
            <div className="bg-gradient-to-r from-indigo-50 to-slate-50 border border-indigo-100 rounded-2xl p-4 shadow-soft">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Settings className="w-5 h-5 text-indigo-600" />
                  <span className="font-medium text-slate-900">Templates & Settings</span>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {/* Load Template Button */}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setLoadTemplateDialogOpen(true)}
                    disabled={templates.length === 0}
                    data-testid="load-template-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Load Template
                    {templates.length > 0 && (
                      <span className="ml-1 text-xs bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded">
                        {templates.length}
                      </span>
                    )}
                  </Button>
                  
                  {/* Save Template Button */}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setSaveTemplateDialogOpen(true)}
                    disabled={Object.keys(rules).length === 0}
                    data-testid="save-template-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Save as Template
                    {templateLimit.limit !== -1 && (
                      <span className="ml-1 text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                        {templateLimit.used}/{templateLimit.limit}
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Onboarding Hint */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-4 shadow-soft">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-slate-900">
                      {guidedModeActive && columnsToClean.length > 0 
                        ? `Cleaning ${columnsToClean.length} columns` 
                        : 'How to clean your data'
                      }
                    </span>
                    <p className="text-sm text-slate-600 mt-1">
                      {guidedModeActive && columnsToClean.length > 0 
                        ? `Configure rules for each column, then click "Validate & Next" to proceed. Preview becomes available after all columns are validated.`
                        : '1. Select a column from the left • 2. Choose cleaning rules • 3. Click "Preview Changes" when ready'
                      }
                    </p>
                    {/* Pro tip for faster processing */}
                    <div className="mt-2 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg w-fit">
                      <Lightbulb className="w-3.5 h-3.5 flex-shrink-0" />
                      <span><strong>Pro tip:</strong> Only include columns you want cleaned — your file will process faster!</span>
                    </div>
                  </div>
                </div>
              </div>

            <div className="grid lg:grid-cols-3 gap-8">
            {/* Column List */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-soft sticky top-28">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-heading font-semibold text-slate-900">
                    Columns ({fileData?.column_count})
                  </h2>
                  <Dialog open={newColumnDialogOpen} onOpenChange={setNewColumnDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="add-column-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        New
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create New Column</DialogTitle>
                        <DialogDescription>
                          Create a new column based on data from an existing column.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>New Column Name</Label>
                          <Input
                            placeholder="e.g., domain, cleaned_website"
                            value={newColumnName}
                            onChange={(e) => setNewColumnName(e.target.value)}
                            data-testid="new-column-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Source Column</Label>
                          <Select value={newColumnSource} onValueChange={setNewColumnSource}>
                            <SelectTrigger data-testid="source-column-select">
                              <SelectValue placeholder="Select source column" />
                            </SelectTrigger>
                            <SelectContent>
                              {fileData?.columns?.map(col => (
                                <SelectItem key={col} value={col}>{col}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Transformation</Label>
                          <Select value={newColumnTransform} onValueChange={setNewColumnTransform}>
                            <SelectTrigger data-testid="transform-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="copy">Copy Value</SelectItem>
                              <SelectItem value="extract_domain">Extract Email Domain</SelectItem>
                              <SelectItem value="extract_website_domain">Extract Website Domain</SelectItem>
                              <SelectItem value="website_clean">Clean Website URL</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setNewColumnDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleAddNewColumn} data-testid="create-column-btn">
                          Create Column
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
                
                <ScrollArea className="h-[400px] pr-4" data-testid="column-list-nav">
                  <div className="space-y-2">
                    {fileData?.columns?.map((col, index) => {
                      const colAnalysis = getColumnAnalysis(col);
                      const hasRules = rules[col]?.length > 0;
                      const hasIssues = colAnalysis?.issues?.length > 0;
                      const hasSuggestions = colAnalysis?.suggested_rules?.length > 0;
                      const columnInfo = fileData?.columns_info?.[col];
                      const isTooLarge = columnInfo?.too_large_for_cleaning;
                      
                      // T05: Check if column is in guided flow
                      const isInGuidedFlow = guidedModeActive && columnsToClean.includes(col);
                      const guidedIndex = columnsToClean.indexOf(col);
                      const isCurrentGuided = isInGuidedFlow && guidedIndex === currentColumnIndex;
                      const isPastGuided = isInGuidedFlow && hasRules;
                      
                      // Define column status for better UX
                      let columnStatus = 'ready';
                      let statusTooltip = 'Ready - no action needed';
                      
                      if (isTooLarge) {
                        columnStatus = 'disabled';
                        statusTooltip = 'Column too large for cleaning';
                      } else if (hasRules) {
                        columnStatus = 'configured';
                        statusTooltip = 'Cleaning rules configured';
                      } else if (hasIssues) {
                        columnStatus = 'action_required';
                        statusTooltip = 'Action required - issues detected';
                      } else if (hasSuggestions) {
                        columnStatus = 'suggested';
                        statusTooltip = 'Suggestions available (optional)';
                      }
                      
                      // In guided mode, only show columns to clean prominently
                      if (guidedModeActive && columnsToClean.length > 0 && !isInGuidedFlow) {
                        // Dim non-selected columns in guided mode
                        return (
                          <button
                            key={col}
                            onClick={() => !isTooLarge && setSelectedColumn(col)}
                            disabled={isTooLarge}
                            className="w-full text-left p-2 rounded-lg transition-all opacity-40 bg-slate-50 border border-transparent hover:opacity-60"
                            data-testid={`column-btn-${col}`}
                            title="Not selected for cleaning"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-mono text-xs truncate text-slate-400">{col}</span>
                            </div>
                          </button>
                        );
                      }
                      
                      return (
                        <button
                          key={col}
                          onClick={() => {
                            if (!isTooLarge) {
                              setSelectedColumn(col);
                              if (isInGuidedFlow) {
                                setCurrentColumnIndex(guidedIndex);
                              }
                            }
                          }}
                          disabled={isTooLarge}
                          className={`w-full text-left p-3 rounded-xl transition-all ${
                            isTooLarge
                              ? 'bg-slate-100 border-2 border-slate-200 opacity-60 cursor-not-allowed'
                              : selectedColumn === col 
                                ? 'bg-indigo-50 border-2 border-indigo-500' 
                                : isInGuidedFlow && !hasRules
                                  ? 'bg-amber-50 border-2 border-amber-200 hover:border-amber-300'
                                  : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
                          }`}
                          data-testid={`column-btn-${col}`}
                          title={statusTooltip}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {/* Guided mode step number */}
                              {isInGuidedFlow && (
                                <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${
                                  hasRules 
                                    ? 'bg-emerald-500 text-white' 
                                    : isCurrentGuided 
                                      ? 'bg-indigo-600 text-white' 
                                      : 'bg-slate-300 text-slate-600'
                                }`}>
                                  {hasRules ? '✓' : guidedIndex + 1}
                                </span>
                              )}
                              <span className={`font-mono text-sm truncate ${isTooLarge ? 'text-slate-400' : ''}`}>{col}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {isTooLarge && (
                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Too large</span>
                              )}
                              {/* Action Required - Orange warning (blocking) */}
                              {columnStatus === 'action_required' && (
                                <div className="relative group">
                                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                    Action required
                                  </span>
                                </div>
                              )}
                              {/* Suggestions Available - Blue info with pulsing effect (non-blocking) */}
                              {columnStatus === 'suggested' && (
                                <div className="relative group">
                                  <div className="relative">
                                    <AlertCircle className="w-4 h-4 text-blue-500" />
                                    <span className="absolute inset-0 animate-ping bg-blue-400 rounded-full opacity-30"></span>
                                  </div>
                                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 font-medium">
                                    Suggestions available
                                  </span>
                                </div>
                              )}
                              {/* Configured - Green check (user has set rules) */}
                              {columnStatus === 'configured' && (
                                <div className="relative group">
                                  <Check className="w-4 h-4 text-emerald-500" />
                                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                    Rules configured
                                  </span>
                                </div>
                              )}
                              {/* Ready - Gray circle (no action needed) */}
                              {columnStatus === 'ready' && (
                                <div className="relative group">
                                  <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                    No action needed
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          {isTooLarge ? (
                            <p className="text-xs text-amber-600 mt-1">
                              Data too large for cleaning (~{columnInfo?.avg_cell_size} chars/cell)
                            </p>
                          ) : colAnalysis && (
                            <p className="text-xs text-slate-500 mt-1">
                              {colAnalysis.data_type} • {colAnalysis.unique_count} unique
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
                
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-600">
                    <span className="font-medium text-indigo-600">{getTotalRulesCount()}</span> rules selected
                  </p>
                </div>
              </div>
            </div>

            {/* Rule Selection */}
            <div className="lg:col-span-2">
              {selectedColumn ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-soft" data-testid="cleaning-options-panel">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-heading font-semibold text-slate-900">
                        <span className="font-mono text-indigo-600">{selectedColumn}</span>
                      </h2>
                      <p className="text-slate-600 text-sm mt-1">
                        Select cleaning rules to apply to this column
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Help/Tour button - launches interactive product tour */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => startProductTour(guidedModeActive)}
                        className="text-slate-500 hover:text-indigo-600"
                        data-testid="cleaning-tour-btn"
                        title="Take the guided tour"
                      >
                        <HelpCircle className="w-4 h-4" />
                      </Button>
                      
                      {getColumnAnalysis(selectedColumn)?.suggested_rules?.length > 0 && (
                        <div className="flex items-center gap-2 text-sm text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-full">
                          <Sparkles className="w-4 h-4" />
                          AI Suggested
                        </div>
                      )}
                      
                      {/* Remove from cleaning button */}
                      {guidedModeActive && columnsToClean.includes(selectedColumn) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            // Get the current index of the selected column
                            const currentIdx = columnsToClean.indexOf(selectedColumn);
                            
                            // Remove from columnsToClean
                            const remainingCols = columnsToClean.filter(c => c !== selectedColumn);
                            setColumnsToClean(remainingCols);
                            
                            // Clear any rules for this column
                            setRules(prev => {
                              const newRules = { ...prev };
                              delete newRules[selectedColumn];
                              return newRules;
                            });
                            
                            // Move to next column (keep position, not jump to first)
                            if (remainingCols.length > 0) {
                              // If there's a column at the same index or next, go there
                              // Otherwise go to the previous column
                              const nextIdx = Math.min(currentIdx, remainingCols.length - 1);
                              setSelectedColumn(remainingCols[nextIdx]);
                              setCurrentColumnIndex(nextIdx);
                            }
                            toast.success(`Removed "${selectedColumn}" from cleaning`);
                          }}
                          className="text-red-500 bg-red-50 hover:bg-red-600 hover:text-white transition-colors"
                          data-testid="remove-column-btn"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Issues */}
                  {getColumnAnalysis(selectedColumn)?.issues?.length > 0 && (
                    <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="flex items-center gap-2 text-amber-800 font-medium mb-2">
                        <AlertTriangle className="w-4 h-4" />
                        Issues Detected
                      </div>
                      <ul className="text-sm text-amber-700 space-y-1">
                        {getColumnAnalysis(selectedColumn)?.issues.map((issue, i) => (
                          <li key={i}>• {issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Sample Values */}
                  <div className="mb-6 p-4 bg-slate-50 rounded-xl" data-testid="sample-values-section">
                    <p className="text-sm text-slate-600 mb-2">Sample values:</p>
                    <div className="flex flex-wrap gap-2">
                      {getColumnAnalysis(selectedColumn)?.sample_values?.length > 0 ? (
                        getColumnAnalysis(selectedColumn)?.sample_values?.slice(0, 5).map((val, i) => (
                          <span 
                            key={i}
                            className="px-3 py-1 bg-white border border-slate-200 rounded-lg text-sm font-mono"
                          >
                            {val || '(empty)'}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-slate-400 italic">
                          Loading sample values...
                        </span>
                      )}
                    </div>
                  </div>

                  {/* AI Suggested Rules */}
                  {getColumnAnalysis(selectedColumn)?.suggested_rules?.length > 0 && (
                    <div className="mb-6" data-testid="ai-suggestions-section">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-indigo-600" />
                          <span className="text-sm font-medium text-indigo-900">
                            AI-suggested cleaning for this column
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Rules that require manual validation (country and state removed - they work fine with defaults)
                              const rulesRequiringValidation = ['date_format', 'dropdown_standardize', 'multi_checkbox_standardize', 'revenue_normalize'];
                              
                              const suggestedRules = getColumnAnalysis(selectedColumn)?.suggested_rules || [];
                              const autoApplicableRules = suggestedRules.filter(r => !rulesRequiringValidation.includes(r));
                              const validationRequiredRules = suggestedRules.filter(r => rulesRequiringValidation.includes(r));
                              
                              // Auto-apply non-validation rules (now includes standardize_country and standardize_state)
                              if (autoApplicableRules.length > 0) {
                                setRules(prev => ({
                                  ...prev,
                                  [selectedColumn]: [...new Set([...(prev[selectedColumn] || []), ...autoApplicableRules])]
                                }));
                              }
                              
                              // Show toast about validation rules
                              if (validationRequiredRules.length > 0) {
                                const ruleNames = validationRequiredRules.map(r => RULE_INFO[r]?.name || r).join(', ');
                                toast.info(`Applied ${autoApplicableRules.length} rules. Please configure: ${ruleNames}`, { duration: 5000 });
                              } else {
                                toast.success(`Applied ${autoApplicableRules.length} AI suggestions`);
                              }
                            }}
                            className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                            data-testid="apply-all-ai-btn"
                          >
                            <Zap className="w-3 h-3 mr-1" />
                            Apply All
                          </Button>
                          {/* Next Column button - only show when rules are selected */}
                          {rules[selectedColumn]?.length > 0 && columnsToClean.length > 1 && (
                            <Button
                              size="sm"
                              onClick={() => {
                                const currentIndex = columnsToClean.indexOf(selectedColumn);
                                const nextIndex = (currentIndex + 1) % columnsToClean.length;
                                setSelectedColumn(columnsToClean[nextIndex]);
                                toast.success(`Moved to "${columnsToClean[nextIndex]}"`);
                              }}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid="next-column-btn"
                            >
                              Next Column
                              <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {getColumnAnalysis(selectedColumn)?.suggested_rules?.map(ruleKey => {
                          // Hide trim_whitespace when certain rules are selected
                          const rulesWithBuiltInTrim = ['date_format', 'phone_format', 'dropdown_standardize', 'standardize_state', 'standardize_country', 'postal_format', 'split_full_name', 'email_fix', 'revenue_normalize'];
                          if (ruleKey === 'trim_whitespace') {
                            const currentRules = rules[selectedColumn] || [];
                            const hasRuleWithBuiltInTrim = currentRules.some(r => rulesWithBuiltInTrim.includes(r));
                            if (hasRuleWithBuiltInTrim) return null;
                          }
                          
                          const ruleData = RULE_INFO[ruleKey];
                          if (!ruleData) return null;
                          const isSelected = rules[selectedColumn]?.includes(ruleKey);
                          
                          return (
                            <div key={ruleKey} className="space-y-2">
                              <div
                                className={`rule-card cursor-pointer border-2 ${isSelected ? 'selected border-indigo-500 bg-indigo-50' : 'border-indigo-200 bg-indigo-50/50'}`}
                                onClick={() => handleRuleToggle(selectedColumn, ruleKey)}
                                data-testid={`rule-${ruleKey}`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-600'}`}>
                                    {ruleData.icon}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-slate-900">{ruleData.name}</span>
                                      {isSelected && <Check className="w-4 h-4 text-indigo-600" />}
                                      {/* Info icon with hover preview */}
                                      {(ruleData.example || ruleData.getExample) && (
                                        <div className="relative group">
                                          <Info className="w-4 h-4 text-slate-400 hover:text-indigo-600 cursor-help" />
                                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-3 bg-slate-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-xl">
                                            <p className="font-semibold mb-2">Preview</p>
                                            <div className="space-y-1">
                                              {(() => {
                                                // Get context-specific example for trim_whitespace
                                                const colAnalysis = getColumnAnalysis(selectedColumn);
                                                const example = ruleData.getExample 
                                                  ? ruleData.getExample(colAnalysis?.data_type) 
                                                  : ruleData.example;
                                                return (
                                                  <>
                                                    <div className="flex items-center gap-2">
                                                      <span className="text-red-300 line-through">{example.before}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                      <ArrowRight className="w-3 h-3 text-slate-400" />
                                                      <span className="text-emerald-300 font-mono">{example.after}</span>
                                                    </div>
                                                  </>
                                                );
                                              })()}
                                            </div>
                                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
                                          </div>
                                        </div>
                                      )}
                                      {/* Validation required badge */}
                                      {ruleData.requiresValidation && (
                                        <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                          Config required
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-slate-500 mt-0.5">{ruleData.description}</p>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Inline configs for suggested rules */}
                              {isSelected && ruleKey === 'standardize_state' && (
                                <div className="ml-8 p-3 bg-slate-50 rounded-lg">
                                  <Label className="text-sm text-slate-600 mb-2 block">Output Format</Label>
                                  <Select 
                                    value={ruleConfigs[`${selectedColumn}_standardize_state`]?.format || globalStateFormat}
                                    onValueChange={(val) => {
                                      handleRuleConfig(selectedColumn, 'standardize_state', 'format', val);
                                      setGlobalStateFormat(val);
                                    }}
                                  >
                                    <SelectTrigger data-testid="state-format-select">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="full">Full Names (California)</SelectItem>
                                      <SelectItem value="abbrev">Abbreviations (CA)</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                              
                              {isSelected && ruleKey === 'dropdown_standardize' && (
                                <div className="ml-8">
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => openDropdownDialog(selectedColumn)}
                                    data-testid="configure-dropdown-btn"
                                  >
                                    <List className="w-4 h-4 mr-2" />
                                    Manage Values
                                  </Button>
                                </div>
                              )}
                              
                              {isSelected && ruleKey === 'revenue_ranges' && (
                                <div className="ml-8">
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => openRevenueDialog(selectedColumn)}
                                    data-testid="configure-revenue-btn"
                                  >
                                    <DollarSign className="w-4 h-4 mr-2" />
                                    Configure Ranges
                                  </Button>
                                </div>
                              )}
                              
                              {/* Date Format Config for Suggested Rules */}
                              {isSelected && ruleKey === 'date_format' && (
                                <div className="ml-8 p-3 bg-slate-50 rounded-lg space-y-3">
                                  <div>
                                    <Label className="text-sm text-slate-600 mb-2 block">Output Date Format</Label>
                                    <Select 
                                      value={ruleConfigs[`${selectedColumn}_date_format`]?.output_format || '%Y-%m-%d'}
                                      onValueChange={(val) => handleRuleConfig(selectedColumn, 'date_format', 'output_format', val)}
                                    >
                                      <SelectTrigger data-testid="date-format-select">
                                        <SelectValue placeholder="Select format" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {DATE_FORMATS.map(fmt => (
                                          <SelectItem key={fmt.value} value={fmt.value}>
                                            {fmt.label} ({fmt.example})
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  
                                  {/* Timestamp Backup Option */}
                                  <div className="flex items-start gap-2 pt-2 border-t border-slate-200">
                                    <Checkbox 
                                      id={`${selectedColumn}_timestamp_backup`}
                                      checked={ruleConfigs[`${selectedColumn}_date_format`]?.include_timestamp_backup || false}
                                      onCheckedChange={(checked) => handleRuleConfig(selectedColumn, 'date_format', 'include_timestamp_backup', checked)}
                                      data-testid="timestamp-backup-checkbox"
                                    />
                                    <div className="flex-1">
                                      <Label 
                                        htmlFor={`${selectedColumn}_timestamp_backup`}
                                        className="text-sm font-medium text-slate-700 cursor-pointer"
                                      >
                                        Add timestamp backup column
                                      </Label>
                                      <p className="text-xs text-slate-500 mt-0.5">
                                        Creates {selectedColumn}_timestamp_ms with Unix milliseconds for reliable parsing
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Multi-Checkbox Config for Suggested Rules */}
                              {isSelected && ruleKey === 'multi_checkbox_standardize' && (
                                <div className="ml-8">
                                  <Button variant="outline" size="sm" onClick={() => openMultiCheckboxDialog(selectedColumn)}>
                                    <List className="w-4 h-4 mr-2" />
                                    Manage Values
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Toggle to show more rules - auto-expand when no AI suggestions */}
                  {getColumnAnalysis(selectedColumn)?.suggested_rules?.length > 0 && (
                    <div className="mb-4">
                      <button
                        onClick={() => setShowAllRules(prev => ({ ...prev, [selectedColumn]: !prev[selectedColumn] }))}
                        className="flex items-center gap-2 text-sm text-slate-600 hover:text-indigo-600 transition-colors"
                        data-testid="show-more-rules-btn"
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${showAllRules[selectedColumn] ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`}>
                          {showAllRules[selectedColumn] && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span>Show all cleaning options</span>
                        <span className="text-xs text-slate-400">
                          ({Object.keys(RULE_INFO).length - (getColumnAnalysis(selectedColumn)?.suggested_rules?.length || 0)} more)
                        </span>
                      </button>
                    </div>
                  )}

                  {/* All Rules - shown when toggled OR when no AI suggestions */}
                  {(showAllRules[selectedColumn] || !getColumnAnalysis(selectedColumn)?.suggested_rules?.length) && (
                    <Tabs defaultValue="all" className="w-full" data-testid="all-rules-section">
                      {!getColumnAnalysis(selectedColumn)?.suggested_rules?.length && (
                        <div className="flex items-center gap-2 mb-3 text-slate-600">
                          <List className="w-4 h-4" />
                          <span className="text-sm font-medium">All cleaning options</span>
                        </div>
                      )}
                      <TabsList className="mb-4 flex-wrap">
                        <TabsTrigger value="all">All</TabsTrigger>
                        <TabsTrigger value="format">Format</TabsTrigger>
                        <TabsTrigger value="standardize">Standardize</TabsTrigger>
                        <TabsTrigger value="custom">Custom</TabsTrigger>
                      </TabsList>
                      
                      {['all', 'format', 'standardize', 'custom', 'text', 'extract'].map(category => (
                        <TabsContent key={category} value={category}>
                          <div className="grid sm:grid-cols-2 gap-4">
                            {Object.entries(RULE_INFO)
                              .filter(([_, data]) => category === 'all' || data.category === category)
                              .filter(([ruleKey]) => !getColumnAnalysis(selectedColumn)?.suggested_rules?.includes(ruleKey))
                              .filter(([ruleKey]) => {
                                // Hide trim_whitespace when certain rules are selected
                                if (ruleKey === 'trim_whitespace') {
                                  const rulesWithBuiltInTrim = ['date_format', 'phone_format', 'dropdown_standardize', 'standardize_state', 'standardize_country', 'postal_format', 'split_full_name', 'email_fix', 'revenue_normalize'];
                                  const currentRules = rules[selectedColumn] || [];
                                  return !currentRules.some(r => rulesWithBuiltInTrim.includes(r));
                                }
                                return true;
                              })
                              .map(([ruleKey, ruleData]) => {
                                const isSelected = rules[selectedColumn]?.includes(ruleKey);
                                
                                return (
                                  <div key={ruleKey} className="space-y-2">
                                    <div
                                      className={`rule-card cursor-pointer ${isSelected ? 'selected' : ''}`}
                                      onClick={() => handleRuleToggle(selectedColumn, ruleKey)}
                                      data-testid={`rule-${ruleKey}`}
                                    >
                                      <div className="flex items-start gap-3">
                                        <div className={`p-2 rounded-lg ${isSelected ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                                          {ruleData.icon}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2">
                                            <span className="font-medium text-slate-900">{ruleData.name}</span>
                                            {isSelected && <Check className="w-4 h-4 text-indigo-600" />}
                                            {/* Info icon with hover preview */}
                                            {ruleData.example && (
                                              <div className="relative group">
                                                <Info className="w-4 h-4 text-slate-400 hover:text-indigo-600 cursor-help" />
                                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-3 bg-slate-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-xl">
                                                  <p className="font-semibold mb-2">Preview</p>
                                                  <div className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                      <span className="text-red-300 line-through">{ruleData.example.before}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                      <ArrowRight className="w-3 h-3 text-slate-400" />
                                                      <span className="text-emerald-300 font-mono">{ruleData.example.after}</span>
                                                    </div>
                                                  </div>
                                                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
                                                </div>
                                              </div>
                                            )}
                                            {ruleData.requiresValidation && (
                                              <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                                Config required
                                              </span>
                                            )}
                                          </div>
                                          <p className="text-xs text-slate-500 mt-0.5">{ruleData.description}</p>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Phone Format Config */}
                                    {isSelected && ruleKey === 'phone_format' && (
                                      <div className="ml-8 p-3 bg-slate-50 rounded-lg space-y-3">
                                        <div className="flex items-center gap-2 text-sm text-slate-600">
                                          <Phone className="w-4 h-4" />
                                          <span>Auto-detects country code from phone format</span>
                                        </div>
                                        <div>
                                          <Label className="text-sm text-slate-600 mb-2 block">Fallback Country</Label>
                                          <Select 
                                            value={ruleConfigs[`${selectedColumn}_phone_format`]?.country_code || 'US'}
                                            onValueChange={(val) => handleRuleConfig(selectedColumn, 'phone_format', 'country_code', val)}
                                          >
                                            <SelectTrigger>
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="US">United States (+1)</SelectItem>
                                              <SelectItem value="UK">United Kingdom (+44)</SelectItem>
                                              <SelectItem value="CA">Canada (+1)</SelectItem>
                                              <SelectItem value="AU">Australia (+61)</SelectItem>
                                              <SelectItem value="FR">France (+33)</SelectItem>
                                              <SelectItem value="DE">Germany (+49)</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Date Format Config */}
                                    {isSelected && ruleKey === 'date_format' && (
                                      <div className="ml-8 p-3 bg-slate-50 rounded-lg space-y-3">
                                        <div>
                                          <Label className="text-sm text-slate-600 mb-2 block">Output Format</Label>
                                          <Select 
                                            value={ruleConfigs[`${selectedColumn}_date_format`]?.output_format || '%Y-%m-%d'}
                                            onValueChange={(val) => handleRuleConfig(selectedColumn, 'date_format', 'output_format', val)}
                                          >
                                            <SelectTrigger>
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {DATE_FORMATS.map(fmt => (
                                                <SelectItem key={fmt.value} value={fmt.value}>
                                                  {fmt.label} ({fmt.example})
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        
                                        {/* Timestamp Backup Option */}
                                        <div className="flex items-start gap-2 pt-2 border-t border-slate-200">
                                          <Checkbox 
                                            id={`${selectedColumn}_all_timestamp_backup`}
                                            checked={ruleConfigs[`${selectedColumn}_date_format`]?.include_timestamp_backup || false}
                                            onCheckedChange={(checked) => handleRuleConfig(selectedColumn, 'date_format', 'include_timestamp_backup', checked)}
                                          />
                                          <div className="flex-1">
                                            <Label 
                                              htmlFor={`${selectedColumn}_all_timestamp_backup`}
                                              className="text-sm font-medium text-slate-700 cursor-pointer"
                                            >
                                              Add timestamp backup column
                                            </Label>
                                            <p className="text-xs text-slate-500 mt-0.5">
                                              Creates {selectedColumn}_timestamp_ms with Unix milliseconds
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* State Format Config */}
                                    {isSelected && ruleKey === 'standardize_state' && (
                                      <div className="ml-8 p-3 bg-slate-50 rounded-lg">
                                        <Label className="text-sm text-slate-600 mb-2 block">Output Format</Label>
                                        <Select 
                                          value={ruleConfigs[`${selectedColumn}_standardize_state`]?.format || globalStateFormat}
                                          onValueChange={(val) => {
                                            handleRuleConfig(selectedColumn, 'standardize_state', 'format', val);
                                            setGlobalStateFormat(val);
                                          }}
                                        >
                                          <SelectTrigger>
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="full">Full Names (California)</SelectItem>
                                            <SelectItem value="abbrev">Abbreviations (CA)</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    )}
                                    
                                    {/* Postal Format Config */}
                                    {isSelected && ruleKey === 'postal_format' && (
                                      <div className="ml-8 p-3 bg-slate-50 rounded-lg">
                                        <div className="flex items-center gap-2">
                                          <Checkbox 
                                            checked={ruleConfigs[`${selectedColumn}_postal_format`]?.auto_split !== false}
                                            onCheckedChange={(checked) => handleRuleConfig(selectedColumn, 'postal_format', 'auto_split', checked)}
                                          />
                                          <Label className="text-sm text-slate-600">Auto-split ZIP+4</Label>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Dropdown Config */}
                                    {isSelected && ruleKey === 'dropdown_standardize' && (
                                      <div className="ml-8">
                                        <Button variant="outline" size="sm" onClick={() => openDropdownDialog(selectedColumn)}>
                                          <List className="w-4 h-4 mr-2" />
                                          Manage Values
                                        </Button>
                                      </div>
                                    )}
                                    
                                    {/* Multi-Checkbox Config */}
                                    {isSelected && ruleKey === 'multi_checkbox_standardize' && (
                                      <div className="ml-8">
                                        <Button variant="outline" size="sm" onClick={() => openMultiCheckboxDialog(selectedColumn)}>
                                          <List className="w-4 h-4 mr-2" />
                                          Manage Values
                                        </Button>
                                      </div>
                                    )}
                                    
                                    {/* Revenue Ranges Config */}
                                    {isSelected && ruleKey === 'revenue_ranges' && (
                                      <div className="ml-8">
                                        <Button variant="outline" size="sm" onClick={() => openRevenueDialog(selectedColumn)}>
                                          <DollarSign className="w-4 h-4 mr-2" />
                                          Configure Ranges
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        </TabsContent>
                      ))}
                    </Tabs>
                  )}
                </div>
              ) : (
                <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-soft">
                  <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Select a column to configure cleaning rules</p>
                </div>
              )}

              {/* Action Buttons - T05 Guided Validation */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-slate-200">
                {/* Validation Progress */}
                {guidedModeActive && columnsToClean.length > 0 && (
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="flex-1 sm:w-48">
                      <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                        <span>Validation Progress</span>
                        <span>{columnsToClean.filter(col => rules[col]?.length > 0).length}/{columnsToClean.length} columns</span>
                      </div>
                      <Progress value={getValidationProgress()} className="h-2" />
                    </div>
                  </div>
                )}
                
                <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                  {/* Next Column Button - Show in guided mode when not all columns are validated */}
                  {guidedModeActive && columnsToClean.length > 0 && !isAllColumnsValidated() && (
                    <Button
                      onClick={handleValidateColumn}
                      disabled={!rules[selectedColumn]?.length}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6"
                      data-testid="next-column-btn"
                    >
                      {currentColumnIndex < columnsToClean.length - 1 ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Validate & Next Column
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Validate Last Column
                        </>
                      )}
                    </Button>
                  )}
                  
                  {/* Preview Button - Disabled until all columns validated in guided mode */}
                  <Button
                    onClick={handlePreview}
                    disabled={getTotalRulesCount() === 0 || previewLoading || !isAllColumnsValidated()}
                    className={`rounded-full px-8 ${
                      isAllColumnsValidated() 
                        ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                    data-testid="preview-changes-btn"
                    title={!isAllColumnsValidated() ? `Validate ${getRemainingColumns().length} more column(s) to continue` : ''}
                  >
                    {previewLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating Preview...
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-2" />
                        Preview Changes
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Validation Message */}
              {guidedModeActive && columnsToClean.length > 0 && !isAllColumnsValidated() && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-800 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>
                      <strong>Validate {getRemainingColumns().length} more column(s)</strong> to enable preview. 
                      {!rules[selectedColumn]?.length && ' Select at least one cleaning rule for the current column.'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 2 && preview && (
          <div className="space-y-6">
            {/* Cleaning Error Banner with Retry */}
            {cleaningError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-800 mb-1">
                      {cleaningError.userFriendlyTitle || 'Cleaning Failed'}
                    </h3>
                    <p className="text-sm text-red-700 mb-3">
                      {cleaningError.userFriendlyMessage || cleaningError.message}
                    </p>
                    {cleaningError.type === 'server_error' && (
                      <p className="text-xs text-red-600 mb-3 bg-red-100 p-2 rounded">
                        💡 <strong>Tip:</strong> Files over 50,000 rows work best when split into smaller parts. 
                        Try using Excel or Google Sheets to split your file first.
                      </p>
                    )}
                    <div className="flex items-center gap-3">
                      {cleaningError.canRetry !== false && (
                        <Button
                          onClick={() => {
                            setCleaningError(null);
                            handleClean();
                          }}
                          size="sm"
                          className="bg-red-600 hover:bg-red-700 text-white"
                          data-testid="retry-cleaning-btn"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          {cleaningError.actionText || 'Retry Cleaning'}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCleaningError(null)}
                        className="text-red-700 border-red-300 hover:bg-red-100"
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-soft">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-heading font-semibold text-slate-900">
                    Preview Changes
                  </h2>
                  <p className="text-slate-600 text-sm mt-1">
                    Review the changes before applying to all data
                  </p>
                </div>
              </div>

              {/* T07: Clear Metrics Section */}
              <div className="grid grid-cols-4 gap-4 mb-6 p-4 bg-slate-50 rounded-xl">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">{preview.original?.length || 10}</p>
                  <p className="text-xs text-slate-500">Sample rows shown</p>
                </div>
                <div className="text-center border-x border-slate-200">
                  <p className="text-2xl font-bold text-indigo-600">{fileData?.processed_row_count?.toLocaleString() || fileData?.row_count?.toLocaleString() || 0}</p>
                  <p className="text-xs text-slate-500">
                    Rows to process
                  </p>
                </div>
                <div className="text-center border-r border-slate-200">
                  <p className="text-2xl font-bold text-emerald-600">
                    {/* Calculate actual rows affected from preview data */}
                    {preview.original && preview.cleaned
                      ? preview.original.filter((row, i) => {
                          const cleanedRow = preview.cleaned[i];
                          return cleanedRow && Object.keys(row).some(col => row[col] !== cleanedRow[col]);
                        }).length * Math.ceil((fileData?.processed_row_count || fileData?.row_count || 0) / (preview.original?.length || 1))
                      : preview.changes_count || 0
                    }
                  </p>
                  <p className="text-xs text-slate-500">Est. rows affected</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">
                    {/* Estimated cells to be cleaned */}
                    {(() => {
                      const totalRows = fileData?.processed_row_count || fileData?.row_count || 0;
                      
                      // Try to count from rules first
                      let totalRulesSelected = Object.values(rules).reduce((sum, columnRules) => {
                        return sum + (Array.isArray(columnRules) ? columnRules.length : 0);
                      }, 0);
                      
                      // Fallback: count columns with changes from preview data
                      if (totalRulesSelected === 0 && preview?.original && preview?.cleaned) {
                        const changedColumns = new Set();
                        preview.original.forEach((row, i) => {
                          const cleanedRow = preview.cleaned[i];
                          if (cleanedRow) {
                            Object.keys(row).forEach(col => {
                              if (row[col] !== cleanedRow[col]) {
                                changedColumns.add(col);
                              }
                            });
                          }
                        });
                        // Also count new columns added in cleaned data
                        preview.cleaned.forEach(cleanedRow => {
                          Object.keys(cleanedRow).forEach(col => {
                            if (!preview.original[0] || !(col in preview.original[0])) {
                              changedColumns.add(col);
                            }
                          });
                        });
                        totalRulesSelected = changedColumns.size;
                      }
                      
                      return (totalRows * totalRulesSelected).toLocaleString();
                    })()}
                  </p>
                  <p className="text-xs text-slate-500">Est. cells to clean</p>
                </div>
              </div>

              {/* Error 520 / Server Error Banner - Above comparison */}
              {cleaningError && (cleaningError.type === 'server_error' || cleaningError.type === 'timeout') && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-amber-800 mb-1">File too large to process</h3>
                      <p className="text-sm text-amber-700 mb-2">
                        Your file has {cleaningError.rowCount?.toLocaleString()} rows which exceeded our processing limit. 
                        Please split it into smaller files (under 50,000 rows each) and upload again.
                      </p>
                      <Button
                        onClick={() => window.open('/tools/split', '_blank')}
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                      >
                        <Scissors className="w-4 h-4 mr-1" />
                        Split File Tool
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Side by Side Preview - No tabs, direct display */}
              {/* Show selected columns + any new columns created by cleaning rules */}
              {(() => {
                // Get columns selected for cleaning
                const selectedCols = columnsToClean.length > 0 
                  ? columnsToClean 
                  : Object.keys(preview.original?.[0] || {});
                
                // Find new columns created by cleaning (exist in cleaned but not in original)
                const originalCols = new Set(Object.keys(preview.original?.[0] || {}));
                const cleanedCols = Object.keys(preview.cleaned?.[0] || {});
                const newColumns = cleanedCols.filter(col => !originalCols.has(col));
                
                // Combine: selected columns + new columns
                const columnsToShow = [...selectedCols, ...newColumns];
                
                if (columnsToShow.length === 0) return null;
                
                return (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Side by Side Comparison</span>
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    {newColumns.length > 0 && (
                      <span className="text-violet-600 mr-2">+{newColumns.length} new column{newColumns.length !== 1 ? 's' : ''}</span>
                    )}
                    <ArrowRight className="w-3 h-3" />
                    Showing {columnsToShow.length} column{columnsToShow.length !== 1 ? 's' : ''}
                  </span>
                </div>
                
                {/* New columns summary banner */}
                {newColumns.length > 0 && (
                  <div className="bg-violet-50 border-b border-violet-200 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-violet-600" />
                      <span className="text-sm font-medium text-violet-800">
                        New column{newColumns.length !== 1 ? 's' : ''} created:
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {newColumns.map(col => (
                          <span key={col} className="inline-flex items-center gap-1 bg-violet-100 text-violet-700 px-2 py-0.5 rounded text-sm font-medium">
                            {col}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="overflow-x-auto" style={{ maxWidth: '100%' }}>
                  <table className="w-full border-collapse" style={{ minWidth: 'max-content' }}>
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="sticky left-0 z-10 bg-slate-100 px-3 py-2 text-left text-xs font-semibold text-slate-600 border-b border-r border-slate-200 w-12">
                          #
                        </th>
                        {columnsToShow.map(col => {
                          const isNewColumn = newColumns.includes(col);
                          return (
                          <th key={col} className={`px-3 py-2 text-left text-xs font-semibold border-b border-slate-200 min-w-[120px] ${isNewColumn ? 'bg-violet-50' : ''}`}>
                            <div className="flex flex-col">
                              <span className={isNewColumn ? 'text-violet-700' : 'text-slate-600'}>
                                {col}
                                {isNewColumn && <span className="ml-1 text-[10px] bg-violet-200 text-violet-700 px-1 rounded">NEW</span>}
                              </span>
                              <span className={isNewColumn ? 'text-violet-500 font-normal' : 'text-emerald-600 font-normal'}>
                                {isNewColumn ? 'Created' : 'After'}
                              </span>
                            </div>
                          </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.original?.map((originalRow, i) => {
                        const cleanedRow = preview.cleaned?.[i] || {};
                        return (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                            <td className="sticky left-0 z-10 px-3 py-2 text-xs text-slate-400 border-r border-slate-200 bg-inherit font-mono">
                              {i + 1}
                            </td>
                            {columnsToShow.map(col => {
                              const isNewColumn = newColumns.includes(col);
                              const originalValue = originalRow[col] || '-';
                              const cleanedValue = cleanedRow[col] || '-';
                              const hasChanged = originalValue !== cleanedValue;
                              
                              return (
                                <td key={col} className={`px-3 py-2 text-sm min-w-[120px] border-b border-slate-100 ${isNewColumn ? 'bg-violet-50' : ''}`}>
                                  {isNewColumn ? (
                                    // New column - show the new value with icon
                                    <div className="flex items-center gap-1">
                                      <span className="text-violet-700 font-medium truncate block max-w-[150px]">
                                        {cleanedValue && cleanedValue !== '-' ? cleanedValue : <span className="text-slate-400 italic">—</span>}
                                      </span>
                                    </div>
                                  ) : hasChanged ? (
                                    <div className="space-y-1">
                                      <div className="text-slate-400 line-through text-xs truncate max-w-[150px]">
                                        {originalValue}
                                      </div>
                                      <div className="text-emerald-700 font-medium truncate max-w-[150px]">
                                        {cleanedValue}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-slate-600 truncate block max-w-[150px]">
                                      {originalValue}
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Scroll hint */}
                {columnsToShow.length > 5 && (
                  <p className="text-xs text-slate-500 mt-2 px-4 pb-2 flex items-center gap-1">
                    <ArrowRight className="w-3 h-3" />
                    Scroll horizontally to see all {columnsToShow.length} columns
                  </p>
                )}
              </div>
                );
              })()}
            </div>

            {/* Actions */}
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => updateStep(1)}
                className="rounded-full px-6"
                data-testid="back-to-rules-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Modify Rules
              </Button>
              
              <Button
                onClick={handleCleanWithConfirmation}
                disabled={cleaning}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-8"
                data-testid="apply-cleaning-btn"
              >
                {cleaning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Cleaning...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Apply to All Data
                  </>
                )}
              </Button>
            </div>
            
            {/* Cleaning Error with File Splitter CTA */}
            {cleaningError && (
              <div className="mt-6 bg-red-50 border border-red-200 rounded-2xl p-6" data-testid="cleaning-error-panel">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-900 text-lg">
                      {cleaningError.userFriendlyTitle || (cleaningError.type === 'limit_reached' ? 'Row Limit Reached' : 'Cleaning Failed')}
                    </h3>
                    <p className="text-red-700 mt-1 text-sm">
                      {cleaningError.userFriendlyMessage || cleaningError.message}
                    </p>
                    
                    {/* Server error specific guidance */}
                    {cleaningError.type === 'server_error' && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800">
                          <strong>What is Error 520?</strong><br/>
                          This happens when our servers are processing too much data at once. 
                          Your file ({cleaningError.rowCount?.toLocaleString()} rows) may be too large to process in one request.
                        </p>
                        <p className="text-sm text-amber-700 mt-2">
                          <strong>What to do:</strong>
                        </p>
                        <ul className="text-sm text-amber-700 list-disc ml-4 mt-1">
                          <li>Wait 30 seconds and try the &quot;Retry&quot; button</li>
                          <li>Or split your file into smaller parts (under 50,000 rows each)</li>
                        </ul>
                      </div>
                    )}
                    
                    {/* Retry button for server errors */}
                    {cleaningError.canRetry && (
                      <div className="mt-4">
                        <Button
                          onClick={() => {
                            setCleaningError(null);
                            handleClean();
                          }}
                          className="bg-red-600 hover:bg-red-700 text-white"
                          data-testid="retry-cleaning-btn-2"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          {cleaningError.actionText || 'Retry Cleaning'}
                        </Button>
                      </div>
                    )}
                    
                    {/* File splitter suggestion for large files */}
                    {cleaningError.rowCount > 10000 && (
                      <div className="mt-4 p-4 bg-white border border-red-100 rounded-xl">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <Scissors className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">Try splitting your file first</p>
                            <p className="text-sm text-slate-600">
                              Split your {cleaningError.rowCount?.toLocaleString()} rows into smaller files, then clean each one separately.
                            </p>
                          </div>
                        </div>
                        <Button
                          onClick={() => window.open('/tools/split', '_blank')}
                          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                          data-testid="open-file-splitter-btn"
                        >
                          <Scissors className="w-4 h-4 mr-2" />
                          Open File Splitter Tool
                          <ExternalLink className="w-4 h-4 ml-2" />
                        </Button>
                        <p className="text-xs text-slate-500 text-center mt-2">
                          Free tool • No login required • Supports files up to 50MB
                        </p>
                      </div>
                    )}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCleaningError(null)}
                      className="mt-4"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Dismiss
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Export */}
        {step === 3 && cleaningResult && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-soft text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                Data Cleaned Successfully!
              </h2>
              <p className="text-slate-600 mb-8">
                Your data is ready to export
              </p>

              {/* Main Stats - 2 columns only */}
              <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-slate-50 rounded-xl">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">{cleaningResult.cleaned_row_count?.toLocaleString() || 0}</p>
                  <p className="text-sm text-slate-500">Rows Processed</p>
                </div>
                <div className="text-center border-l border-slate-200">
                  <p className="text-2xl font-bold text-emerald-600">
                    {/* Cells cleaned - use actual stats or estimate from rows * columns */}
                    {(() => {
                      // Try actual stats first
                      let cells = cleaningResult.time_saved?.cells_cleaned || 0;
                      if (cells === 0 && cleaningResult.column_stats) {
                        cells = Object.entries(cleaningResult.column_stats)
                          .filter(([k]) => k !== '__new_columns_created__')
                          .reduce((sum, [, s]) => sum + (s?.cleaned || 0), 0);
                        // Add new columns populated
                        const newCols = cleaningResult.column_stats.__new_columns_created__;
                        if (newCols) {
                          cells += Object.values(newCols).reduce((sum, s) => sum + (s?.populated || 0), 0);
                        }
                      }
                      // Estimate if still 0: rows * number of unique columns with rules
                      if (cells === 0) {
                        const rows = cleaningResult.cleaned_row_count || 0;
                        // Count unique columns from rules
                        let uniqueCols = new Set();
                        if (cleaningResult.rules) {
                          cleaningResult.rules.forEach(r => {
                            if (r.column) uniqueCols.add(r.column);
                          });
                        }
                        const cols = uniqueCols.size || Object.keys(rules).length || 1;
                        cells = Math.round(rows * cols * 0.7); // Estimate 70% of cells affected
                      }
                      return cells.toLocaleString();
                    })()}
                  </p>
                  <p className="text-sm text-slate-500">Est. Cells Cleaned</p>
                </div>
              </div>

              {/* Estimated Time Saved - New Excel Expert Model with Transparency */}
              <div className="mb-8 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Clock className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-bold text-emerald-800">
                          ~{(() => {
                            // Use the new time_saved model from backend
                            const totalSeconds = cleaningResult.time_saved?.total_seconds || 0;
                            const hours = totalSeconds / 3600;
                            const minutes = totalSeconds / 60;
                            const days = hours / 8;
                            
                            if (days >= 1) {
                              return `${Math.round(hours)} hours (${days.toFixed(1)} working days)`;
                            } else if (hours >= 1) {
                              return `${hours.toFixed(1)} hours`;
                            } else {
                              return `${Math.max(1, Math.round(minutes))} minutes`;
                            }
                          })()} saved
                        </p>
                        {/* Info icon - Opens transparency modal */}
                        <button 
                          onClick={() => setShowTimeSavingsModal(true)}
                          className="p-1 rounded-full hover:bg-emerald-200 transition-colors"
                          data-testid="time-savings-info-btn"
                          title="How is this calculated?"
                        >
                          <HelpCircle className="w-4 h-4 text-emerald-500 hover:text-emerald-700 cursor-pointer" />
                        </button>
                      </div>
                      <p className="text-sm text-emerald-600">
                        vs. manual Excel cleaning ({cleaningResult.time_saved?.total_rules || cleaningResult.rules?.length || 0} rules × {cleaningResult.cleaned_row_count?.toLocaleString() || 0} rows)
                      </p>
                      {/* Completed time - total elapsed time from upload to apply */}
                      <p className="text-xs text-emerald-500 mt-2 flex items-center gap-1">
                        <span>⚡</span>
                        <span>Completed in {(() => {
                          // Use user_elapsed_time (upload to apply), fallback to elapsed_time (frontend tracked), then processing_duration
                          const seconds = cleaningResult.user_elapsed_time_seconds || cleaningResult.elapsed_time_seconds || cleaningResult.processing_duration_seconds || 0;
                          if (seconds < 1) return '< 1s';
                          if (seconds < 60) return `${Math.round(seconds)}s`;
                          const mins = Math.floor(seconds / 60);
                          const secs = Math.round(seconds % 60);
                          return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
                        })()}</span>
                      </p>
                    </div>
                  </div>
                  
                  {/* Share Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShareDialogOpen(true)}
                    className="text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                    data-testid="share-result-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Share
                  </Button>
                </div>
              </div>

              {/* Column-level cleaning stats */}
              {cleaningResult.column_stats && Object.keys(cleaningResult.column_stats).filter(k => k !== '__new_columns_created__').length > 0 && (
                <div className="mb-8 p-4 bg-indigo-50 rounded-xl text-left">
                  <h3 className="text-sm font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Cleaning Summary by Column
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(cleaningResult.column_stats)
                      .filter(([column]) => column !== '__new_columns_created__')
                      .map(([column, stats]) => (
                      <div key={column} className="flex items-center justify-between p-2 bg-white rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-700">{column}</span>
                          <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-600 rounded-full">
                            {RULE_INFO[stats.rule]?.name || stats.rule}
                          </span>
                        </div>
                        <div className="text-sm">
                          <span className="font-semibold text-emerald-600">{stats.cleaned?.toLocaleString() || 0}</span>
                          <span className="text-slate-400"> of </span>
                          <span className="text-slate-600">{cleaningResult.cleaned_row_count?.toLocaleString()}</span>
                          <span className="text-slate-400 ml-1">rows updated</span>
                          {cleaningResult.cleaned_row_count > 0 && stats.cleaned !== undefined && (
                            <span className="ml-2 text-xs text-slate-500">
                              ({Math.round((stats.cleaned / cleaningResult.cleaned_row_count) * 100)}%)
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Columns Created Section */}
              {cleaningResult.column_stats?.__new_columns_created__ && 
               Object.keys(cleaningResult.column_stats.__new_columns_created__).length > 0 && (
                <div className="mb-8 p-4 bg-violet-50 rounded-xl text-left">
                  <h3 className="text-sm font-semibold text-violet-900 mb-3 flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    New Columns Created
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(cleaningResult.column_stats.__new_columns_created__).map(([columnName, stats]) => (
                      <div key={columnName} className="flex items-center justify-between p-2 bg-white rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-700">{columnName}</span>
                          <span className="text-xs px-2 py-0.5 bg-violet-100 text-violet-600 rounded-full">
                            {RULE_INFO[stats.source_rule]?.name || stats.source_rule}
                          </span>
                        </div>
                        <div className="text-sm">
                          <span className="font-semibold text-violet-600">{stats.populated.toLocaleString()}</span>
                          <span className="text-slate-400 ml-1">rows populated</span>
                          {cleaningResult.cleaned_row_count > 0 && (
                            <span className="ml-2 text-xs text-slate-500">
                              ({Math.round((stats.populated / cleaningResult.cleaned_row_count) * 100)}%)
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-violet-600 mt-3">
                    ✨ These columns have been added to your exported file
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {cleaningResult?.cleaned_row_count > 30000 ? (
                  <>
                    <Button
                      onClick={() => handleExport('zip')}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8"
                      data-testid="export-zip-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download ZIP (Recommended)
                    </Button>
                    <p className="text-sm text-slate-500 text-center">
                      Large file ({cleaningResult?.cleaned_row_count?.toLocaleString()} rows) - ZIP contains chunked CSVs for easier processing
                    </p>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => handleExport('xlsx')}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8"
                      data-testid="export-xlsx-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Excel (.xlsx)
                    </Button>
                    <Button
                      onClick={() => handleExport('csv')}
                      variant="outline"
                      className="rounded-full px-8"
                      data-testid="export-csv-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download CSV
                    </Button>
                  </>
                )}
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200 flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="text-slate-600 rounded-full px-6"
                  data-testid="back-to-dashboard-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/upload')}
                  className="text-indigo-600"
                  data-testid="clean-another-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Clean Another File
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Manual Mapping Dialog - Enhanced */}
      <Dialog open={mappingDialogOpen} onOpenChange={setMappingDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Configure Value Mappings</DialogTitle>
            <DialogDescription>
              Map values from &quot;{mappingColumn}&quot; to standardized options
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden py-4 space-y-4">
            {/* Quick Load Presets */}
            <div className="p-4 bg-indigo-50 rounded-lg">
              <Label className="text-sm font-medium mb-3 block text-indigo-900">Quick Load Presets</Label>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => loadPresetOptions('us_states')} data-testid="load-us-states">
                  <MapPin className="w-3 h-3 mr-1" /> US States
                </Button>
                <Button size="sm" variant="outline" onClick={() => loadPresetOptions('canadian_provinces')} data-testid="load-canadian">
                  <MapPin className="w-3 h-3 mr-1" /> Canadian Provinces
                </Button>
                <Button size="sm" variant="outline" onClick={() => loadPresetOptions('all_states')} data-testid="load-all-states">
                  <MapPin className="w-3 h-3 mr-1" /> All States/Provinces
                </Button>
                <Button size="sm" variant="outline" onClick={() => loadPresetOptions('countries')} data-testid="load-countries">
                  <Globe className="w-3 h-3 mr-1" /> Countries
                </Button>
              </div>
              
              {/* Industry Presets */}
              <div className="mt-3 pt-3 border-t border-indigo-200">
                <Label className="text-xs text-indigo-700 mb-2 block">Industry Presets</Label>
                <div className="flex flex-wrap gap-2">
                  {presets?.industry_presets && Object.entries(presets.industry_presets).slice(0, 6).map(([key, preset]) => (
                    <Button 
                      key={key} 
                      size="sm" 
                      variant="ghost" 
                      className="h-7 text-xs"
                      onClick={() => loadPresetOptions(key)}
                      data-testid={`load-${key}`}
                    >
                      <Building2 className="w-3 h-3 mr-1" /> {preset.name}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Paste Options */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <Label className="text-sm font-medium mb-2 block">Paste Options (one per line or comma-separated)</Label>
              <div className="flex gap-2">
                <Textarea
                  placeholder="Option 1&#10;Option 2&#10;Option 3&#10;or: Option 1, Option 2, Option 3"
                  value={pasteOptions}
                  onChange={(e) => setPasteOptions(e.target.value)}
                  className="min-h-[60px] flex-1"
                  data-testid="paste-options-input"
                />
                <Button onClick={handlePasteOptions} className="self-end" data-testid="paste-options-btn">
                  <Clipboard className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
            </div>
            
            {/* Add single option */}
            <div className="flex gap-2">
              <Input
                placeholder="Add single option..."
                value={newMappingOption}
                onChange={(e) => setNewMappingOption(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addMappingOption()}
                data-testid="new-mapping-option-input"
              />
              <Button onClick={addMappingOption} data-testid="add-option-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Current options */}
            {mappingOptions.length > 0 && (
              <div className="p-3 bg-emerald-50 rounded-lg">
                <Label className="text-xs text-emerald-700 mb-2 block">Available Options ({mappingOptions.length})</Label>
                <div className="flex flex-wrap gap-2 max-h-[80px] overflow-y-auto">
                  {mappingOptions.map((opt, i) => (
                    <span key={i} className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded text-xs flex items-center gap-1">
                      {opt}
                      <button 
                        onClick={() => setMappingOptions(prev => prev.filter((_, idx) => idx !== i))}
                        className="hover:text-emerald-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Value mappings */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Map Your Values</Label>
              <ScrollArea className="h-[200px] border border-slate-200 rounded-lg p-2">
                <div className="space-y-2">
                  {Object.keys(mappings).map((value, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 bg-white border border-slate-100 rounded-lg">
                      <span className="font-mono text-sm flex-1 truncate text-slate-700" title={value}>
                        {value}
                      </span>
                      <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <Select
                        value={mappings[value] || ''}
                        onValueChange={(val) => setMappings(prev => ({ ...prev, [value]: val }))}
                      >
                        <SelectTrigger className="w-[180px]" data-testid={`mapping-select-${i}`}>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__keep_original__">-- Keep Original --</SelectItem>
                          {mappingOptions.map((opt, j) => (
                            <SelectItem key={j} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMappingDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveMappings} data-testid="save-mappings-btn">
              Save Mappings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revenue Ranges Dialog */}
      <RevenueRangesDialog
        open={revenueDialogOpen}
        onOpenChange={setRevenueDialogOpen}
        revenueTargetType={revenueTargetType}
        setRevenueTargetType={selectRevenueTargetType}
        revenueCurrencyFallback={revenueCurrencyFallback}
        setRevenueCurrencyFallback={setRevenueCurrencyFallback}
        selectedRevenueRanges={selectedRevenueRanges}
        setSelectedRevenueRanges={setSelectedRevenueRanges}
        revenueSuggestions={revenueSuggestions}
        customRangeInput={customRangeInput}
        setCustomRangeInput={setCustomRangeInput}
        onSave={saveRevenueRanges}
      />

      {/* Revenue Normalize Dialog */}
      <Dialog open={revenueNormalizeDialogOpen} onOpenChange={setRevenueNormalizeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Revenue Normalize Settings</DialogTitle>
            <DialogDescription>
              Configure how revenue values should be normalized for &quot;{revenueNormalizeColumn}&quot;
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <Label className="text-sm font-medium mb-2 block">Default Currency</Label>
              <p className="text-xs text-slate-500 mb-3">Used when currency is not specified in the data</p>
              <Select value={revenueNormalizeCurrency} onValueChange={setRevenueNormalizeCurrency}>
                <SelectTrigger className="w-full" data-testid="normalize-currency-select">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">$ USD - US Dollar</SelectItem>
                  <SelectItem value="EUR">€ EUR - Euro</SelectItem>
                  <SelectItem value="GBP">£ GBP - British Pound</SelectItem>
                  <SelectItem value="CAD">$ CAD - Canadian Dollar</SelectItem>
                  <SelectItem value="AUD">$ AUD - Australian Dollar</SelectItem>
                  <SelectItem value="CHF">CHF - Swiss Franc</SelectItem>
                  <SelectItem value="JPY">¥ JPY - Japanese Yen</SelectItem>
                  <SelectItem value="INR">₹ INR - Indian Rupee</SelectItem>
                  <SelectItem value="CNY">¥ CNY - Chinese Yuan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-sm text-emerald-800">
                <strong>What this does:</strong> Converts revenue values like &quot;1.2M&quot;, &quot;$50,000&quot;, or &quot;250k&quot; into clean numbers (1200000, 50000, 250000) and creates a separate Currency column.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevenueNormalizeDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveRevenueNormalize} data-testid="save-normalize-btn">
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dropdown Standardization Dialog */}
      <DropdownStandardizeDialog
        open={dropdownDialogOpen}
        onOpenChange={setDropdownDialogOpen}
        columnName={dropdownColumn}
        values={dropdownValues}
        editingValueIndex={editingValueIndex}
        setEditingValueIndex={setEditingValueIndex}
        editingNewValue={editingNewValue}
        setEditingNewValue={setEditingNewValue}
        onRename={handleDropdownRename}
        onMerge={handleDropdownMerge}
        onDelete={handleDropdownDelete}
        onRestore={handleDropdownRestore}
        onSave={saveDropdownStandardization}
      />

      {/* Multi-Checkbox Standardization Dialog */}
      <Dialog open={multiCheckboxDialogOpen} onOpenChange={setMultiCheckboxDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Standardize Multi-Checkbox Values</DialogTitle>
            <DialogDescription>
              View, rename, merge, or delete values separated by semicolon (;) in &quot;{multiCheckboxColumn}&quot;
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden py-4 space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-4 gap-4 p-3 bg-slate-50 rounded-lg text-sm">
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-800">{multiCheckboxValues.length}</p>
                <p className="text-slate-500">Total Values</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-600">{multiCheckboxValues.filter(v => v.action === 'keep').length}</p>
                <p className="text-slate-500">Keep</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{multiCheckboxValues.filter(v => v.action === 'rename' || v.action === 'merge').length}</p>
                <p className="text-slate-500">Modify</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{multiCheckboxValues.filter(v => v.action === 'delete').length}</p>
                <p className="text-slate-500">Delete</p>
              </div>
            </div>
            
            {/* Value List */}
            <ScrollArea className="h-[400px] border border-slate-200 rounded-lg">
              <div className="p-2 space-y-2">
                {multiCheckboxValues.map((item, index) => (
                  <div 
                    key={index} 
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      item.action === 'delete' ? 'bg-red-50 border-red-200' :
                      item.action === 'rename' || item.action === 'merge' ? 'bg-blue-50 border-blue-200' :
                      'bg-white border-slate-200'
                    }`}
                  >
                    {/* Original Value */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-mono text-sm truncate ${item.action === 'delete' ? 'line-through text-red-500' : ''}`}>
                          {item.original}
                        </span>
                        {(item.action === 'rename' || item.action === 'merge') && (
                          <>
                            <ArrowRight className="w-4 h-4 text-blue-500 flex-shrink-0" />
                            <span className="font-mono text-sm text-blue-700 font-medium truncate">{item.display}</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {item.action !== 'keep' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMultiCheckboxRestore(index)}
                          className="text-slate-500 hover:text-slate-700"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      ) : (
                        <>
                          {/* Rename */}
                          {editingMultiValueIndex === index ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={editingMultiNewValue}
                                onChange={(e) => setEditingMultiNewValue(e.target.value)}
                                className="h-8 w-32"
                                placeholder="New value"
                                autoFocus
                              />
                              <Button
                                size="sm"
                                onClick={() => handleMultiCheckboxRename(index, editingMultiNewValue)}
                                className="h-8"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setEditingMultiValueIndex(null); setEditingMultiNewValue(''); }}
                                className="h-8"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setEditingMultiValueIndex(index); setEditingMultiNewValue(item.original); }}
                                title="Rename"
                              >
                                <Edit3 className="w-4 h-4" />
                              </Button>
                              
                              {/* Merge */}
                              <Select onValueChange={(val) => handleMultiCheckboxMerge(index, val)}>
                                <SelectTrigger className="w-8 h-8 p-0 border-0 hover:bg-slate-100">
                                  <Merge className="w-4 h-4 text-slate-500" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__merge_placeholder__" disabled>Merge into...</SelectItem>
                                  {multiCheckboxValues
                                    .filter((v, i) => i !== index && v.action !== 'delete')
                                    .map((v, i) => (
                                      <SelectItem key={i} value={v.original}>{v.original}</SelectItem>
                                    ))
                                  }
                                </SelectContent>
                              </Select>
                              
                              {/* Delete */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMultiCheckboxDelete(index)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMultiCheckboxDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveMultiCheckboxStandardization} data-testid="save-multi-checkbox-btn">
              Apply Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* State Format Confirmation Dialog */}
      <Dialog open={stateFormatDialogOpen} onOpenChange={setStateFormatDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Choose State/Province Format</DialogTitle>
            <DialogDescription>
              How would you like state and province names to be formatted?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6 space-y-3">
            <button
              onClick={() => confirmStateFormat('full')}
              className={`w-full p-4 rounded-xl border-2 transition-all text-left hover:border-indigo-400 ${
                globalStateFormat === 'full' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'
              }`}
              data-testid="state-format-full"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-slate-900">Full Names</div>
                  <p className="text-sm text-slate-500 mt-1">
                    California, New York, Ontario, British Columbia
                  </p>
                </div>
                {globalStateFormat === 'full' && <Check className="w-5 h-5 text-indigo-600" />}
              </div>
            </button>
            
            <button
              onClick={() => confirmStateFormat('abbrev')}
              className={`w-full p-4 rounded-xl border-2 transition-all text-left hover:border-indigo-400 ${
                globalStateFormat === 'abbrev' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'
              }`}
              data-testid="state-format-abbrev"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-slate-900">Abbreviations</div>
                  <p className="text-sm text-slate-500 mt-1">
                    CA, NY, ON, BC
                  </p>
                </div>
                {globalStateFormat === 'abbrev' && <Check className="w-5 h-5 text-indigo-600" />}
              </div>
            </button>
          </div>
          
          <p className="text-xs text-slate-500 text-center">
            This setting applies to all state/province columns in your file
          </p>
        </DialogContent>
      </Dialog>
      
      {/* Save Template Dialog */}
      <Dialog open={saveTemplateDialogOpen} onOpenChange={setSaveTemplateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save as Template</DialogTitle>
            <DialogDescription>
              Save your current cleaning rules to reuse on future files.
              {templateLimit.limit !== -1 && (
                <span className="block mt-1 text-indigo-600">
                  {templateLimit.used}/{templateLimit.limit} templates used
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label>Template Name *</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., CRM Contact Cleanup"
                className="mt-1"
                data-testid="template-name-input"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="What this template is for..."
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p className="font-medium text-slate-700 mb-2">Rules to save:</p>
              <ul className="space-y-1 text-slate-600">
                {Object.entries(rules).map(([col, colRules]) => (
                  colRules.length > 0 && (
                    <li key={col} className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                      {col}: {colRules.map(r => RULE_INFO[r]?.name || r).join(', ')}
                    </li>
                  )
                ))}
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveTemplateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveTemplate} data-testid="save-template-confirm-btn">
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Load Template Dialog */}
      <Dialog open={loadTemplateDialogOpen} onOpenChange={setLoadTemplateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Load Template</DialogTitle>
            <DialogDescription>
              Choose a saved template to apply to your file. This will replace current rules.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {templates.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <p>No templates saved yet.</p>
                <p className="text-sm mt-1">Save your first template to reuse it later.</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {templates.map((template) => (
                  <div 
                    key={template.template_id}
                    className="border border-slate-200 rounded-lg p-4 hover:border-indigo-300 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-900">{template.name}</h4>
                        {template.description && (
                          <p className="text-sm text-slate-500 mt-1">{template.description}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-2">
                          {template.rules?.length || 0} rules • Created {new Date(template.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => loadTemplate(template)}
                          data-testid={`load-template-${template.template_id}`}
                        >
                          Apply
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => deleteTemplate(template.template_id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoadTemplateDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      
      {/* Share Results Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-emerald-600" />
              Share Your Results
            </DialogTitle>
            <DialogDescription>
              Download your stats as an image or copy the text to share on social media
            </DialogDescription>
          </DialogHeader>
          
          {/* Shareable Stats Card - Will be captured as image */}
          <div 
            ref={shareableStatsRef}
            className="p-6 bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 rounded-xl text-white"
          >
            <div className="text-center mb-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                <FileSpreadsheet className="w-6 h-6" />
                <span className="font-bold text-lg">AI Data Cleaner</span>
              </div>
              <p className="text-indigo-200 text-sm">CRM Data Cleaning Results</p>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-white/10 rounded-lg">
                <p className="text-2xl font-bold">{cleaningResult?.cleaned_row_count?.toLocaleString() || 0}</p>
                <p className="text-xs text-indigo-200">Rows Cleaned</p>
              </div>
              <div className="text-center p-3 bg-white/10 rounded-lg">
                <p className="text-2xl font-bold text-emerald-300">
                  {cleaningResult?.column_stats 
                    ? Object.entries(cleaningResult.column_stats)
                        .filter(([k]) => k !== '__new_columns_created__')
                        .reduce((sum, [, s]) => sum + (s.cleaned || 0), 0).toLocaleString()
                    : 0
                  }
                </p>
                <p className="text-xs text-indigo-200">Cells Fixed</p>
              </div>
              <div className="text-center p-3 bg-white/10 rounded-lg">
                <p className="text-2xl font-bold text-amber-300">
                  ~{cleaningResult?.time_saved 
                    ? (cleaningResult.time_saved.total_hours >= 1 
                      ? `${cleaningResult.time_saved.total_hours}h` 
                      : `${Math.max(1, cleaningResult.time_saved.total_minutes)}m`)
                    : '1h'
                  }
                </p>
                <p className="text-xs text-indigo-200">Time Saved</p>
              </div>
            </div>
            
            {/* Session Duration - Time from upload to success */}
            {sessionDuration && (
              <div className="text-center py-2 bg-white/5 rounded-lg mx-4 mb-3">
                <p className="text-xs text-indigo-300">
                  ⚡ Completed in {
                    sessionDuration >= 60000 
                      ? `${Math.floor(sessionDuration / 60000)}m ${Math.floor((sessionDuration % 60000) / 1000)}s`
                      : `${Math.floor(sessionDuration / 1000)}s`
                  }
                </p>
              </div>
            )}
            
            <div className="text-center pt-3 border-t border-white/20">
              <p className="text-sm text-indigo-200 mb-1">Clean your CRM data in minutes</p>
              <p className="font-medium">aidatacleaner.xyz{user?.referral_code ? `?ref=${user.referral_code}` : ''}</p>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  setDownloadingImage(true);
                  const dataUrl = await htmlToImage.toPng(shareableStatsRef.current, {
                    quality: 1,
                    pixelRatio: 2,
                    backgroundColor: '#4f46e5'
                  });
                  
                  // Create download link
                  const link = document.createElement('a');
                  link.download = 'cleaning-results.png';
                  link.href = dataUrl;
                  link.click();
                  
                  toast.success('Image downloaded! Share it on LinkedIn or Twitter');
                } catch (error) {
                  console.error('Failed to generate image:', error);
                  toast.error('Failed to generate image');
                } finally {
                  setDownloadingImage(false);
                }
              }}
              disabled={downloadingImage}
              className="flex-1"
              data-testid="download-stats-image-btn"
            >
              {downloadingImage ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download Image
            </Button>
            
            <Button
              onClick={() => {
                const timeText = cleaningResult?.time_saved 
                  ? (cleaningResult.time_saved.total_hours >= 1 
                    ? `${cleaningResult.time_saved.total_hours} hours` 
                    : `${cleaningResult.time_saved.total_minutes} minutes`)
                  : `${Math.max(1, Math.round((cleaningResult?.cleaned_row_count * Object.keys(rules).length * 0.5) / 60))} hours`;
                
                const referralLink = user?.referral_code 
                  ? `aidatacleaner.xyz?ref=${user.referral_code}` 
                  : 'aidatacleaner.xyz';
                
                const shareText = `I cleaned ${cleaningResult?.cleaned_row_count?.toLocaleString() || 0} CRM rows and saved ~${timeText} of manual work using AI Data Cleaner 🧹

Messy data → clean imports in minutes.

Try it free: ${referralLink}`;
                
                navigator.clipboard.writeText(shareText);
                toast.success('Copied to clipboard with your referral link!');
              }}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              data-testid="copy-share-text-btn"
            >
              <Clipboard className="w-4 h-4 mr-2" />
              Copy Text
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Cleaning Options Tour Dialog */}
      <Dialog open={showCleaningTour} onOpenChange={setShowCleaningTour}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-indigo-600" />
              How Cleaning Works
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {cleaningTourStep === 0 && (
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-indigo-50 rounded-lg">
                  <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Select Columns</h4>
                    <p className="text-sm text-slate-600">Choose which columns need cleaning from the left panel</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-8 h-8 bg-slate-300 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
                  <div>
                    <h4 className="font-medium text-slate-700">Pick Cleaning Rules</h4>
                    <p className="text-sm text-slate-500">AI suggests rules based on your data type</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-8 h-8 bg-slate-300 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
                  <div>
                    <h4 className="font-medium text-slate-700">Preview Changes</h4>
                    <p className="text-sm text-slate-500">See before/after comparison before applying</p>
                  </div>
                </div>
              </div>
            )}
            
            {cleaningTourStep === 1 && (
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900 mb-2">Understanding Rule Types</h4>
                <div className="grid gap-2">
                  <div className="flex items-center gap-2 p-2 bg-indigo-50 rounded border-l-4 border-indigo-500">
                    <Sparkles className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm"><strong>AI Suggested</strong> - Recommended based on your data</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-amber-50 rounded border-l-4 border-amber-500">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <span className="text-sm"><strong>Validation Required</strong> - Need your input (dropdowns, dates)</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded border-l-4 border-emerald-500">
                    <Check className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm"><strong>Auto-Apply</strong> - Safe to apply automatically</span>
                  </div>
                </div>
              </div>
            )}
            
            {cleaningTourStep === 2 && (
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900 mb-2">Pro Tips</h4>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <Zap className="w-4 h-4 text-indigo-600 mt-0.5" />
                    <p className="text-sm text-slate-600">Use <strong>&quot;Apply All&quot;</strong> to quickly select AI suggestions</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-indigo-600 mt-0.5" />
                    <p className="text-sm text-slate-600">Some rules create new columns (e.g., Split Name → First Name + Last Name)</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 text-indigo-600 mt-0.5" />
                    <p className="text-sm text-slate-600">Preview shows ~100 rows. Full file is processed when you confirm.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              {[0, 1, 2].map(i => (
                <button
                  key={i}
                  onClick={() => setCleaningTourStep(i)}
                  className={`w-2 h-2 rounded-full ${cleaningTourStep === i ? 'bg-indigo-600' : 'bg-slate-300'}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              {cleaningTourStep > 0 && (
                <Button variant="outline" size="sm" onClick={() => setCleaningTourStep(prev => prev - 1)}>
                  Back
                </Button>
              )}
              {cleaningTourStep < 2 ? (
                <Button size="sm" onClick={() => setCleaningTourStep(prev => prev + 1)}>
                  Next
                </Button>
              ) : (
                <Button size="sm" onClick={() => { setShowCleaningTour(false); setCleaningTourStep(0); }}>
                  Got it!
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Download Progress Dialog */}
      <Dialog open={downloadProgress.show} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm" hideClose>
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Download className="w-8 h-8 text-indigo-600 animate-bounce" />
            </div>
            <h3 className="font-semibold text-lg text-slate-900 mb-2">
              {downloadProgress.progress < 100 
                ? `Preparing ${downloadProgress.format.toUpperCase()}...`
                : 'Download Complete!'
              }
            </h3>
            <Progress value={downloadProgress.progress} className="h-2 mb-4" />
            <p className="text-sm text-slate-500 mb-4">{Math.round(downloadProgress.progress)}%</p>
            
            {/* Fun CRM Facts */}
            {downloadProgress.progress < 100 && (
              <div className="p-3 bg-indigo-50 rounded-lg">
                <p className="text-xs text-indigo-600 font-medium mb-1">💡 CRM Fact</p>
                <DownloadFunFact />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* T05: Column Selection Dialog */}
      <Dialog open={columnSelectionOpen} onOpenChange={setColumnSelectionOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-600" />
              Which columns do you want to clean?
            </DialogTitle>
            <DialogDescription>
              Select the columns that need cleaning. You will configure rules for each one before previewing.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {/* Quick Select Options - At the Top */}
            <div className="mb-4 pb-4 border-b border-slate-200 flex flex-wrap gap-2">
              <Button 
                className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-md hover:shadow-lg transition-all"
                size="sm"
                onClick={() => {
                  // Select columns with issues or suggestions
                  const recommended = fileData?.columns?.filter(col => {
                    const colAnalysis = getColumnAnalysis(col);
                    return colAnalysis?.issues?.length > 0 || colAnalysis?.suggested_rules?.length > 0;
                  }) || [];
                  setColumnsToClean(recommended);
                }}
                data-testid="select-recommended-btn"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                Select Recommended
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setColumnsToClean(fileData?.columns || [])}
              >
                Select All
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setColumnsToClean([])}
              >
                Clear Selection
              </Button>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {fileData?.columns?.map(col => {
                const colAnalysis = getColumnAnalysis(col);
                const isSelected = columnsToClean.includes(col);
                const hasSuggestions = colAnalysis?.suggested_rules?.length > 0;
                const hasIssues = colAnalysis?.issues?.length > 0;
                
                return (
                  <button
                    key={col}
                    onClick={() => toggleColumnToClean(col)}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      isSelected 
                        ? 'border-indigo-500 bg-indigo-50' 
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                    data-testid={`column-select-${col}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-5 h-5 rounded flex items-center justify-center border-2 ${
                        isSelected 
                          ? 'bg-indigo-600 border-indigo-600' 
                          : 'border-slate-300'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="font-mono text-sm truncate flex-1">{col}</span>
                    </div>
                    
                    {/* Show hints */}
                    <div className="mt-2 flex items-center gap-2">
                      {hasIssues && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                          Issues found
                        </span>
                      )}
                      {hasSuggestions && !hasIssues && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                          Suggestions
                        </span>
                      )}
                      {colAnalysis && (
                        <span className="text-xs text-slate-500">
                          {colAnalysis.data_type}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          
          <DialogFooter className="flex-shrink-0 pt-4 border-t border-slate-200">
            <div className="flex items-center justify-between w-full">
              <Button 
                variant="ghost" 
                onClick={handleSkipColumnSelection}
                className="text-slate-500"
              >
                Skip (Free-form mode)
              </Button>
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-600">
                  {columnsToClean.length} column(s) selected
                </span>
                <Button 
                  onClick={handleColumnSelectionConfirm}
                  disabled={columnsToClean.length === 0}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="confirm-column-selection-btn"
                >
                  Start Cleaning
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Feature Guide Modals - First-time tutorials for advanced features */}
      <FeatureGuideModal
        key={featureGuideOpen || 'closed'}
        featureKey={featureGuideOpen}
        isOpen={!!featureGuideOpen}
        onClose={() => {
          const currentFeature = featureGuideOpen;
          setFeatureGuideOpen(null);
          
          // After guide closes, open the actual dialog
          if (currentFeature === 'dropdown_standardize' && dropdownColumn) {
            _openDropdownDialogInternal(dropdownColumn);
          } else if (currentFeature === 'multi_checkbox' && multiCheckboxColumn) {
            _openMultiCheckboxDialogInternal(multiCheckboxColumn);
          } else if (currentFeature === 'revenue_ranges' && revenueColumn) {
            _openRevenueDialogInternal(revenueColumn);
          }
        }}
        onDontShowAgain={() => {
          // Guide already marks itself as seen when checkbox is checked
        }}
      />
      
      {/* Floating Support Button */}
      <SupportButton />

      {/* Time Savings Transparency Modal */}
      <TimeSavingsModal
        isOpen={showTimeSavingsModal}
        onClose={() => setShowTimeSavingsModal(false)}
        timeSaved={cleaningResult?.time_saved}
        rowCount={cleaningResult?.cleaned_row_count}
      />
    </div>
  );
};

export default CleaningPage;
